<template>
  <div>
    <div id="silo-result-table">

      <silo-result-table-controls
        :silo="silo"
        :silo_keywords="silo_keywords"
        :selectKeywordForContentReportOrder="selectKeywordForContentReportOrder"
        :unselectKeywordForContentReportOrder="unselectKeywordForContentReportOrder"
        :isWhiteGloveUser="isWhiteGloveUser"
        :site="site"
        :isCalculating="isCalculating"
        :showAdvancedMetrics.sync="showAdvancedMetrics"
        :searchTerm.sync="searchTerm"
        :hideableColumns="hideableColumns"
        :hiddenColumnsKeys="hiddenColumnsKeys"
        :isColumnVisible="isColumnVisible"
        :setColumnVisibility="setColumnVisibility"
        :resetColumnVisibility="resetColumnVisibility"
        :showGraphChart.sync="showGraphChart"
        :visibleTableColumns="visibleTableColumns"
        :sortedTableData="sortedTableData"
        :generatePDF="generatePDF"
        :deleted_silo_keywords="deleted_silo_keywords"
        :isQuickContentOrderView="isQuickContentOrderView"
        :isDeletedView.sync="isDeletedView"
        :selectAllChecked="isAllSelected"
        :selectAllIndeterminate="selectedSiloKeywords.length > 0 && !isAllSelected"
        :selectedCount="selectedSiloKeywords.length"
        :selectAllDisabled="isSelectionDisabled || isDeletedView || !silo_keywords.length"
        :isContentOrderEditable="isContentOrderEditable"
        :isContentOrderInCheckout="isContentOrderInCheckout"
        :isContentOrderInCart="isContentOrderInCart"
        :selectedKeywordsForOrder="selectedKeywordsForOrder"
        :isKeywordSelectionValid="isValidKeywordSelection(selectedKeywordsForOrder)"
        :isMissionControlSession="isMissionControlSession"
        :isValidKeywordSelection="isValidKeywordSelection"
        :selectedSiloKeywordsRows="selectedSiloKeywordsRows"
        :orderContentForSelectedKeywords="orderContentForSelectedKeywords"
        @toggle-select-all="toggleAllSiloKeywords"
        @toggle-select-all-for-order="toggleAllKeywordsForOrder"
        @open-content-order-form="showContentOrderFormModal = true"
      />

      <!-- POP Authority plan view: the package quota strip sits here (design pa-plan.jsx) -->
      <slot name="below-controls"></slot>

      <div class="mt-3">
        <template v-if="!isCalculating">
          <horizontal-two-scroll-bar>
            <div v-if="!showGraphChart" class="voltage-result-area mt-2 mb-2 pb-0" :class="{ 'voltage-result-area--pa': isPopAuthorityPlanView }">
            <b-table
              
              id="silo-result-table"
              class="voltage-table"
              ref="voltage-table"
              :data="isDeletedView ? deleted_silo_keywords : sortedTableDataWithKeywordTypeGroupRows"
              custom-row-key="id"
              :row-class="siloKeywordTypeGroupRowClass"
              :default-sort-direction="'desc'"
              :default-sort="['keywordType', 'desc']"
              @sort="onSort"
              backend-sorting
            >
              <template #header="{ column }">
                <div v-if="column.label === 'Track'" class="pt-5 pb-5"></div>
                <div v-else-if="column.field === 'page_category'" class="pt-5 pb-5" style="text-wrap: nowrap;">
                  <v-tooltip top color="transparent">
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs" v-on="on">
                        {{ column.label }}
                      </span>
                    </template>
                    <div class="white-bg-tooltip small-tooltip p-3">
                      <span class="tooltip-heading">{{ column.label }}</span><br /><br />
                      <span class="tooltip-discription">
                        Page category is based on keyword intent. Different intent types may call for different types of content and landing pages to better satisfy your users’ needs. In this column, you can select page types from the dropdown lists below and our team will know what pages to create based on your selection.
                      </span>
                    </div>
                  </v-tooltip>
                </div>
                <div v-else-if="column.field === 'order_reports'" class="pt-5 pb-5 pl-3 pr-3" style="text-wrap: nowrap;">
                  <v-tooltip top color="transparent">
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs" v-on="on">
                        {{ column.label }}
                      </span>
                    </template>
                    <div class="white-bg-tooltip small-tooltip p-3">
                      <span class="tooltip-heading">{{ column.label }}</span><br /><br />
                      <span class="tooltip-discription">
                        Indicates whether this page has already been ordered.
                      </span>
                    </div>
                  </v-tooltip>
                </div>
                <div v-else class="pt-5 pb-5" style="text-wrap: nowrap;">
                  <template v-if="column?.meta?.tooltipText">
                    <v-tooltip top color="transparent">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs" v-on="on">
                          {{ column.label }}
                        </span>
                      </template>
                      <div class="white-bg-tooltip small-tooltip p-3">
                        <span class="tooltip-heading">{{ column?.meta?.tooltipHeading ? column?.meta?.tooltipHeading : column.label }}</span><br /><br />
                        <span class="tooltip-discription" v-html="column?.meta?.tooltipText"></span>
                      </div>
                    </v-tooltip>
                  </template>
                  <template v-else>
                    {{ column.label }}
                  </template>
                </div>
              </template>
              <template slot-scope="props">
                <b-table-column
                  centered
                  field="hiddenDefaultSortColumn"
                  label="hiddenDefaultSortColumn"
                  :visible="false"
                  sortable
                  :custom-sort="handleDefaultSort"
                >
                  -
                </b-table-column>
                <!-- POP Authority content plans select through the single "Write" column below (design
                     pa-plan.jsx), so this column (client order checkbox / mission-control bulk selection) is
                     not rendered for them -->
                <b-table-column v-if="!isPopAuthorityPlanView && (['user_editing'].includes(silo?.content_report_order?.order_status) || isMissionControlSession)" centered field="track" label="Track" :meta="getColumnMeta('track')" sticky>
                  <template v-if="!isDeletedView && !props.row.__isKeywordTypeGroupHeader">
                    <!-- for mission control checkbox only controls selection for in page actions -->
                    <v-checkbox
                      v-if="isMissionControlSession"
                      class="wg-result-select-all-checkbox ma-0 pt-0"
                      color="success"
                      off-icon="none"
                      on-icon="check"
                      :ripple="false"
                      hide-details
                      :disabled="isSelectionDisabled"
                      :input-value="selectedSiloKeywords.includes(props.row.id)"
                      @change="() => toggleSelectedSiloKeyword(props.row.id)"
                    />
                    <!-- for actual users, the checkbox controls the selected keywords for order -->
                    <v-checkbox
                      v-else
                      class="wg-result-select-all-checkbox ma-0 pt-0"
                      color="success"
                      off-icon="none"
                      on-icon="check"
                      :ripple="false"
                      hide-details
                      :input-value="selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0"
                      @change="() => selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0 ? unselectKeywordForContentReportOrder(props.row) : selectKeywordForContentReportOrder(props.row)"
                    />
                  </template>
                </b-table-column>

                <!-- POP Authority content plan: "Write" checkbox (design pa-plan.jsx): the client or the
                     campaign manager picks the keywords the package covers; locked once the month is billed -->
                <b-table-column
                  v-if="isPopAuthorityPlanView"
                  :visible="!isDeletedView"
                  centered
                  field="pa_write"
                  label="Write"
                  sticky
                >
                  <button
                    v-if="!props.row.__isKeywordTypeGroupHeader"
                    type="button"
                    class="pa-write"
                    :class="{ 'pa-write--sel': paIsSelected(props.row), 'pa-write--locked': !isContentOrderEditable }"
                    role="checkbox"
                    :aria-checked="paIsSelected(props.row) ? 'true' : 'false'"
                    :aria-label="`Write ${props.row.keyword}`"
                    :disabled="!isContentOrderEditable"
                    @click="togglePaSelection(props.row)"
                  >
                    <span class="pa-write__box"><i v-if="paIsSelected(props.row)" class="material-icons" aria-hidden="true">check</i></span>
                  </button>
                </b-table-column>

                <!-- Order reports -->
                <!------------------->

                <b-table-column
                  v-if="isWhiteGloveUser && (isPopAuthorityPlanView || ['in_checkout', 'in_cart_checkout', 'to_be_paid_by_invoice', 'paid'].includes(silo?.content_report_order?.order_status) || isMissionControlSession) && !(isQuickContentOrderView && !['to_be_paid_by_invoice', 'paid'].includes(silo?.content_report_order?.order_status))"
                  :visible="!isDeletedView"
                  field="order_reports"
                  label="Order status"
                  sortable
                >
                  <div v-if="!props.row.__isKeywordTypeGroupHeader" class="flex pl-3 pr-3" style="gap: 8px; align-items: center;">
                    <div class="flex" style="flex: 1; gap: 8px; align-items: center;">
                      <!-- POP Authority content plan: the selection is the package quota (no ordering);
                           status text only, the "Write" checkbox does the selecting (design) -->
                      <template v-if="isPopAuthorityPlanView">
                        <div
                          v-if="selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0"
                          class="ordered-label"
                        >
                          <div class="ordered-label__status-row">
                            <span class="ordered-label__dot" aria-hidden="true"></span>
                            <span class="ordered-label__status">{{ paSelectionLocked ? 'Ordered' : 'Selected' }}</span>
                          </div>
                          <!-- ordered: the option the order carries (what the Asana task says), the package's
                               human edited draft page when the catalogue has not loaded -->
                          <span class="ordered-label__type">{{ paSelectionLocked ? (getContentReportType(props.row) || 'Human edited draft page') : paCycleLabel }}</span>
                        </div>
                        <span v-else style="color: var(--v-gray36Color-base, #9e9fa1); font-weight: 600;">Not selected</span>
                      </template>
                      <div
                        class="ordered-label"
                        v-else-if="['to_be_paid_by_invoice', 'paid'].includes(silo?.content_report_order?.order_status) && selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0"
                      >
                        <div class="ordered-label__status-row">
                          <span class="ordered-label__dot" aria-hidden="true"></span>
                          <span class="ordered-label__status">Ordered</span>
                        </div>
                        <span class="ordered-label__type">{{ getContentReportType(props.row) }}</span>
                      </div>
                      <v-btn
                        v-else-if="selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0 && isMissionControlSession"
                        outlined
                        small
                        color="success"
                        class="button-with-icon text-none content-order-keyword-select selected"
                        :disabled="!isContentOrderEditable"
                        @click="unselectKeywordForContentReportOrder(props.row)"
                      >
                        <i class="material-icons">check</i>
                        <span>Selected for order</span>
                      </v-btn>
                      <v-btn
                        v-else-if="!allOrderedSiloKeywords.includes(props.row.keyword) && isMissionControlSession"
                        outlined
                        small
                        color="mainColor"
                        class="button-with-icon text-none content-order-keyword-select"
                        :disabled="!isContentOrderEditable"
                        @click="selectKeywordForContentReportOrder(props.row)"
                      >
                        <span>Select for order</span>
                      </v-btn>
                    </div>

                    <!--
                    <v-menu
                      offset-y
                      :close-on-content-click="false"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :color="getKeywordNoteTextFromOrder(props.row.id)?'#FF5700':'#C7C7C7'"
                          class="content-order-keyword-note-btn"
                        >
                          <span v-if="getKeywordNoteTextFromOrder(props.row.id)" class="badge">1</span>
                          <i class="material-icons-outlined">textsms</i>
                        </v-btn>
                      </template>
                      <div class="pa-3" style="width: 300px; background-color: var(--v-whiteColor-base)">
                        <v-textarea
                          label="Leave a note for us"
                          :value="getKeywordNoteInputValue(props.row.id)"
                          @input="onKeywordNoteTextChange(props.row.id, $event)"
                        ></v-textarea>
                        <v-btn
                          outlined
                          small
                          color="mainColor"
                          class="button-with-icon text-none"
                          :disabled="!hasUnsavedKeywordNoteChanges(props.row.id)"
                          @click="saveKeywordNoteChanges(props.row.id)">
                          <span>Save</span>
                        </v-btn>
                      </div>
                    </v-menu>
                    -->
                  </div>
                </b-table-column>

                <b-table-column
                  field="keyword"
                  label="Keyword"
                  sortable
                  class="keyword-cell"
                  sticky
                  :meta="getColumnMeta('keyword')"
                >
                  <div
                    v-if="props.row.__isKeywordTypeGroupHeader"
                    class="keyword-type-group-header-keyword silo-keyword-type-group-header-label"
                  >
                    <strong>{{ props.row.__groupLabel }} ({{ props.row.__groupCount }})</strong>
                  </div>
                  <div
                    v-else
                    class="icon-text-container" 
                    style="width: 200px;"
                    @mouseover="currentIdx = props.row.id" @mouseleave="currentIdx = null"
                  >
                    <template v-if="!isDeletedView && (!isWhiteGloveUser || (isWhiteGloveUser && isMissionControlSession))">
                      <v-tooltip v-if="trackedKeywords.includes(props.row.keyword)" top color="transparent">
                        <template v-slot:activator="{ on, attrs }">
                          <span 
                            v-bind="attrs" 
                            v-on="on" 
                            @click="() => {
                              toggleTrackUntrackKeyword(false, props.row)
                            }"
                            class="cursor-pointer"
                          >
                            <i class="material-icons" style="color: var(--v-green7Color-base);">check_circle</i>
                          </span>
                        </template>
                        <div class="white-bg-tooltip small-tooltip tooltip-arrow">
                          <span>Tracked Keyword</span>
                        </div>
                      </v-tooltip>
                      <b-tooltip
                        v-else
                        label="Track keyword"
                        type="is-dark"
                      >
                        <v-menu
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                            >
                              <i class="material-icons add-icon grayColor--text">add_circle</i>
                            </span>
                          </template>
                          <v-list>
                            <v-list-item style="border-bottom: 1px solid var(--v-grayColor-base);">
                              <b>Track keyword</b>
                            </v-list-item>
                            <v-list-item
                              style="border-bottom: 1px solid var(--v-grayColor-base); gap: 10px;"
                              @click="toggleTrackUntrackKeyword(true, props.row)"
                            >
                              Add to All Keywords <span class="tag">DEFAULT</span>
                            </v-list-item>
                            <v-list-item
                              v-for="listName in trackKeywordLists"
                              :key="listName"
                              style="border-bottom: 1px solid var(--v-grayColor-base); gap: 5px;"
                              @click="toggleTrackUntrackKeyword(true, props.row, listName=listName)"
                            >
                              Add to <b>{{listName}}</b> list
                            </v-list-item>
                            <v-list-item
                              style="padding-top: 6px;"
                            >
                              <v-btn
                                outlined block color="grayColor" class="text-none"
                                @click="showAddToNewTrackListModalFor = props.row; newListFieldValue = '';"
                              >
                                <i class="material-icons-outlined" style="margin-left: -8px; color: var(--v-mainColor3-base);">add</i>
                                <span style="color: var(--v-mainColor3-base);">Create new list</span>
                              </v-btn>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </b-tooltip>
                    </template>
                    <span :style="{ fontWeight: props.row.keywordData.isTopLevelKeyword ? '700' : undefined }">
                      {{ props.row.keyword }}
                    </span>
             
                    <span
                      v-if="!isDeletedView && !isQuickContentOrderView"
                      :style="currentIdx !== props.row.id?'visibility:hidden;':''"
                      :class="['material-icons mr-2', { 'cursor-pointer': !(keywordsOngoingRemoval.indexOf(props.row.id) >= 0) }]"
                      @click="
                        !(keywordsOngoingRemoval.indexOf(props.row.id) >= 0)
                          ? removeKeyword(props.row.id)
                          : undefined
                      "
                    >
                      <v-tooltip top color="transparent">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              <i class="material-icons" style="color: var(--v-profileAnchorColor-base);">close</i>
                            </span>
                          </template>
                          <div class="white-bg-tooltip small-tooltip tooltip-arrow">
                            <span>Remove</span>
                          </div>
                        </v-tooltip>
                    </span>
                  </div>
                </b-table-column>
                <b-table-column centered field="keywordType" label="Type" :meta="getColumnMeta('keywordType')" sortable>
                  <div v-if="!props.row.__isKeywordTypeGroupHeader" style="display: flex; flex-direction: row; gap: 10px;">
                    <v-menu
                      v-if="(
                        isContentOrderEditable
                      )"
                      offset-y
                      :disabled="keywordsOngoingLabelUpdate.indexOf(props.row.id) >= 0"
                    >
                      <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                        <v-tooltip
                          v-if="props.row.keywordData.isTopLevelKeyword || props.row.keywordData.isSupportingKeyword"
                          top
                          color="transparent"
                        >
                          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                            <span
                              v-bind="Object.assign({}, tooltipAttrs, menuAttrs)"
                              v-on="Object.assign({}, tooltipOn, menuOn)"
                              class="box-container cursor-pointer"
                            >
                              <span
                                v-if="props.row.keywordData.isTopLevelKeyword && (props.row.keywordData.isSecondaryKeyword || (!!primaryTopLevelKeywords.length && !props.row.keywordData.isPrimaryKeyword))"
                                class="wg-content-plan-detail__tag wg-content-plan-detail__tag--stl"
                              >
                                STL
                                <i
                                  v-if="keywordsOngoingLabelUpdate.indexOf(props.row.id) < 0"
                                  class="fa fa-angle-down wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                                <i
                                  v-else
                                  class="fa fa-spinner fa-spin wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                              </span>
                              <span
                                v-else-if="props.row.keywordData.isTopLevelKeyword"
                                class="wg-content-plan-detail__tag wg-content-plan-detail__tag--tl"
                              >
                                TL
                                <i
                                  v-if="keywordsOngoingLabelUpdate.indexOf(props.row.id) < 0"
                                  class="fa fa-angle-down wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                                <i
                                  v-else
                                  class="fa fa-spinner fa-spin wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                              </span>
                              <span
                                v-else
                                class="wg-content-plan-detail__tag wg-content-plan-detail__tag--s"
                              >
                                S
                                <i
                                  v-if="keywordsOngoingLabelUpdate.indexOf(props.row.id) < 0"
                                  class="fa fa-angle-down wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                                <i
                                  v-else
                                  class="fa fa-spinner fa-spin wg-content-plan-detail__tag__chevron"
                                  aria-hidden="true"
                                />
                              </span>
                            </span>
                          </template>
                          <div class="white-bg-tooltip small-tooltip p-3 tooltip-arrow">
                            <template v-if="props.row.keywordData.isTopLevelKeyword">
                              <span class="tooltip-heading">{{ props.row.keywordData.isPrimaryKeyword ? 'Primary ':''}}{{ props.row.keywordData.isSecondaryKeyword ? 'Secondary ':''}}Top-level keyword</span><br /><br />
                              <span class="tooltip-discription" v-if="props.row.keywordData.isPrimaryKeyword">
                                The primary topic you want to win. Guides the main page (longer, more optimized) and sets the direction for the whole silo.
                              </span>
                              <span class="tooltip-discription" v-else-if="props.row.keywordData.isSecondaryKeyword || (!!primaryTopLevelKeywords.length && !props.row.keywordData.isPrimaryKeyword)">
                                Your backup/companion term. Helps expand coverage and suggests useful subtopics.
                              </span>
                              <span class="tooltip-discription" v-else>
                                A topic you want to win. Guides the main page (longer, more optimized) and sets the direction for the whole silo.
                              </span>
                            </template>
                            <template v-else>
                              <span class="tooltip-heading">Supporting keyword</span><br /><br/>
                              <span class="tooltip-discription">
                                This is a keyword that you can use to<br />
                                create supporting pages and point<br />
                                their links to your main target page.
                              </span>
                            </template>
                          </div>
                        </v-tooltip>
                        <b-button
                          v-else
                          class="keyword-label-dropdown-button"
                          :loading="keywordsOngoingLabelUpdate.indexOf(props.row.id) >= 0"
                          type="is-ghost"
                          size="is-small"
                          :icon-right="keywordsOngoingLabelUpdate.indexOf(props.row.id) >= 0 ? undefined : 'angle-down'"
                          v-bind="menuAttrs"
                          v-on="menuOn"
                        />
                      </template>
                      <v-list>
                        <v-list-item v-if="!props.row.keywordData.isTopLevelKeyword || (props.row.keywordData.isTopLevelKeyword && !props.row.keywordData.isPrimaryKeyword)" @click="updateKeywordLabel(props.row.id, 'PT')">Mark as the Primary Top-level Keyword</v-list-item>
                        <v-list-item v-if="!props.row.keywordData.isTopLevelKeyword || (props.row.keywordData.isTopLevelKeyword && !props.row.keywordData.isSecondaryKeyword)" @click="updateKeywordLabel(props.row.id, 'ST')">Mark as a Secondary Top-level Keyword</v-list-item>
                        <v-list-item v-if="!props.row.keywordData.isSupportingKeyword" @click="updateKeywordLabel(props.row.id, 'S')">Mark as a Supporting Keyword</v-list-item>
                      </v-list>
                    </v-menu>

                    <v-tooltip
                      v-else-if="props.row.keywordData.isTopLevelKeyword || props.row.keywordData.isSupportingKeyword"
                      top
                      color="transparent"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="box-container cursor-pointer">
                          <template v-if="props.row.keywordData.isTopLevelKeyword">
                            <span
                              v-if="props.row.keywordData.isSecondaryKeyword || (!!primaryTopLevelKeywords.length && !props.row.keywordData.isPrimaryKeyword)"
                              class="wg-content-plan-detail__tag wg-content-plan-detail__tag--stl"
                            >STL</span>
                            <span
                              v-else
                              class="wg-content-plan-detail__tag wg-content-plan-detail__tag--tl"
                            >TL</span>
                          </template>
                          <span
                            v-else
                            class="wg-content-plan-detail__tag wg-content-plan-detail__tag--s"
                          >S</span>
                        </span>
                      </template>
                      <div class="white-bg-tooltip small-tooltip p-3 tooltip-arrow">
                        <template v-if="props.row.keywordData.isTopLevelKeyword">
                          <span class="tooltip-heading">{{ props.row.keywordData.isPrimaryKeyword ? 'Primary ':''}}{{ props.row.keywordData.isSecondaryKeyword ? 'Secondary ':''}}Top-level keyword</span><br /><br />
                          <span class="tooltip-discription" v-if="props.row.keywordData.isPrimaryKeyword">
                            The primary topic you want to win. Guides the main page (longer, more optimized) and sets the direction for the whole silo.
                          </span>
                          <span class="tooltip-discription" v-else-if="props.row.keywordData.isSecondaryKeyword || (!!primaryTopLevelKeywords.length && !props.row.keywordData.isPrimaryKeyword)">
                            Your backup/companion term. Helps expand coverage and suggests useful subtopics.
                          </span>
                          <span class="tooltip-discription" v-else>
                            A topic you want to win. Guides the main page (longer, more optimized) and sets the direction for the whole silo.
                          </span>
                        </template>
                        <template v-else>
                          <span class="tooltip-heading">Supporting keyword</span><br /><br/>
                          <span class="tooltip-discription">
                            This is a keyword that you can use to<br />
                            create supporting pages and point<br />
                            their links to your main target page.
                          </span>
                        </template>
                      </div>
                    </v-tooltip>

                  </div>
                  
                </b-table-column>
                
                <!-- VoltageTable columns -->
                <!-------------------------->
      
                <b-table-column
                  numeric
                  field="keywordData.pageOpportunityScore"
                  label="Page Opportunity Score"
                  sortable
                  :visible="isColumnVisible('keywordData.pageOpportunityScore')"
                  :meta="getColumnMeta('keywordData.pageOpportunityScore')"
                >
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <b-tag
                      v-if="props.row.keywordData.pageOpportunityScore"
                      :style="{
                        ...scoreTierToColorStyles(
                          props.row.keywordData.pageOpportunityScoreTier,
                        ),
                        ...{ width: '60px' },
                      }"
                    >
                      {{ props.row.keywordData.pageOpportunityScore }}
                    </b-tag>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column
                  numeric
                  field="keywordData.opportunityScore"
                  label="Keyword Opportunity Score"
                  sortable
                  :visible="isColumnVisible('keywordData.opportunityScore')"
                  :meta="getColumnMeta('keywordData.opportunityScore')"
                >
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <b-tooltip v-if="props.row.keywordData.isBestOpportunityKeyword" label="Best opportunity keyword" type="is-dark">
                      <b-tag type="is-success" style="
                        margin-right: 10px;
                        padding: 0.25em;
                        height: initial;
                      ">
                        <i class="material-icons-outlined" style="font-size: 1em; line-height: 1em;">grade</i>
                      </b-tag>
                    </b-tooltip>
                    <template v-if="props.row.keywordData.opportunityScore === undefined">
                      -
                    </template>
                    <span v-else>{{ props.row.keywordData.opportunityScore }} %</span>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column field="keywordData.monthlySearch" label="Search volume" sortable numeric :visible="isColumnVisible('keywordData.monthlySearch')" :meta="getColumnMeta('keywordData.monthlySearch')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <template v-if="props.row.keywordData.monthlySearch !== undefined">
                      {{ props.row.keywordData.monthlySearch }}
                    </template>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column field="keywordData.impressionsShare" label="Search impressions share %" sortable numeric :visible="isColumnVisible('keywordData.impressionsShare')" :meta="getColumnMeta('keywordData.impressionsShare')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <template v-if="props.row.keywordData.impressionsShare !== undefined">
                      {{ props.row.keywordData.impressionsShare }} %
                    </template>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.impressions" label="Impressions" sortable :visible="isColumnVisible('keywordData.impressions')" :meta="getColumnMeta('keywordData.impressions')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    {{ props.row.keywordData.impressions }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.clicks" label="Clicks" sortable :visible="isColumnVisible('keywordData.clicks')" :meta="getColumnMeta('keywordData.clicks')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    {{ props.row.keywordData.clicks }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.ctr" label="CTR" sortable :visible="isColumnVisible('keywordData.ctr')" :meta="getColumnMeta('keywordData.ctr')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    {{ props.row.keywordData.ctr }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column field="keywordData.position" label="Avg Position (SC)" sortable numeric :visible="isColumnVisible('keywordData.position')" :meta="getColumnMeta('keywordData.position')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    {{ props.row.keywordData.position }}
                  </template>
                  </template>
                </b-table-column>
      
                <!-- KeywordResearchResult columns -->
                <!----------------------------------->
      
                <b-table-column numeric field="keywordData.keyword_intent" label="Intent" sortable :visible="isColumnVisible('keywordData.keyword_intent')" :meta="getColumnMeta('keywordData.keyword_intent')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <b-icon v-if="props.row.keywordResearchDataTaskId"
                      pack="fa"
                      icon="spinner"
                      custom-class="fa-spin mr-4">
                    </b-icon>
                    <template v-else-if="props.row.keywordData.keyword_intent">
                      <v-tooltip top color="black">
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            class="tag"
                            style="text-transform: uppercase; color: var(--v-blackColor-base); font-weight: 700;"
                            v-bind="attrs" v-on="on"
                          >
                            <template v-if="props.row.keywordData.keyword_intent === 'informational'">Inf</template>
                            <template v-else-if="props.row.keywordData.keyword_intent === 'navigational'">Nav</template>
                            <template v-else-if="props.row.keywordData.keyword_intent === 'commercial'">Com</template>
                            <template v-else-if="props.row.keywordData.keyword_intent === 'transactional'">Tran</template>
                            <template v-else>{{props.row.keywordData.keyword_intent}}</template>
                          </span>
                        </template>
                        <div style="max-width: 300px;">
                          <template v-if="props.row.keywordData.keyword_intent === 'informational'">
                            <b>Informational</b><br/>
                            The person just wants to learn or understand something. They're not looking to buy or do anything yet.<br/>
                            <b>Example:</b> <i>“How to boil an egg”</i><br/>
                            <i>They’re curious or asking a question.</i>
                          </template>
                          <template v-else-if="props.row.keywordData.keyword_intent === 'navigational'">
                            <b>Navigational</b><br/>
                            The person wants to go to a specific website or page they already have in mind.<br/>
                            <b>Example:</b> <i>“YouTube” or “Gmail login”</i><br/>
                            <i>They know where they want to go.</i>
                          </template>
                          <template v-else-if="props.row.keywordData.keyword_intent === 'commercial'">
                            <b>Commercial</b><br/>
                            The person is thinking about buying but wants to compare options or find the best deal first.<br/>
                            <b>Example:</b> <i>“Best phone for gaming 2024”</i><br/>
                            <i>They’re doing research before deciding.</i>
                          </template>
                          <template v-else-if="props.row.keywordData.keyword_intent === 'transactional'">
                            <b>Transactional</b><br/>
                            The person is ready to buy something, sign up, or take action.<br/>
                            <b>Example:</b> <i>“Buy iPhone 15”</i><br/>
                            <i>They’re ready to do something now.</i>
                          </template>
                        </div>
                      </v-tooltip>
                    </template>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.keywordScore" label="Keyword score" sortable :visible="isColumnVisible('keywordData.keywordScore')" :meta="getColumnMeta('keywordData.keywordScore')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template>
                    <b-icon v-if="props.row.keywordResearchDataTaskId"
                      pack="fa"
                      icon="spinner"
                      custom-class="fa-spin mr-4">
                    </b-icon>
                    <span v-else-if="![undefined, null].includes(props.row.keywordData.keywordScore)" class="tag" :style="{...keywordScoreToColorStyles(props.row.keywordData.keywordScore), 'justify-content': 'end'}">
                      {{ Math.round(props.row.keywordData.keywordScore) }}
                    </span>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.ta_score" label="T&A Score" sortable :visible="isColumnVisible('keywordData.ta_score')" :meta="getColumnMeta('keywordData.ta_score')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                    <template v-if="props.row.keywordData.ta_score !== undefined && props.row.keywordData.ta_score !== null">
                      {{ props.row.keywordData.ta_score }}
                    </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.KGRScore" label="KGR Score" sortable :visible="isColumnVisible('keywordData.KGRScore')" :meta="getColumnMeta('keywordData.KGRScore')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    <div class="flex align-center" style="justify-content: end;">
                      {{ props.row.keywordData.KGRScore }}
                      <span
                        class="bullet kgr-score-bullet ml-2"
                        :class="{
                          best: props.row.keywordData.KGRScore <= 2.5,
                          good: props.row.keywordData.KGRScore > 2.5 && props.row.keywordData.KGRScore <= 5,
                          normal: props.row.keywordData.KGRScore > 5
                        }"
                      >
                      </span>
                    </div>
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.realCompMajesticTitleAndAnchorComp" label="SEO Competitiveness Index (SCI)" sortable :visible="isColumnVisible('keywordData.realCompMajesticTitleAndAnchorComp')" :meta="getColumnMeta('keywordData.realCompMajesticTitleAndAnchorComp')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                  pack="fa"
                  icon="spinner"
                  custom-class="fa-spin">
                </b-icon>
                <template v-else>
                  {{ props.row.keywordData.realCompMajesticTitleAndAnchorComp }}
                </template>
                  </template>
              </b-table-column>
              <b-table-column numeric field="keywordData.allintitle" label="All In Title" sortable :visible="isColumnVisible('keywordData.allintitle')" :meta="getColumnMeta('keywordData.allintitle')">
                <template v-if="!props.row.__isKeywordTypeGroupHeader">
                <b-icon v-if="props.row.keywordResearchDataTaskId"
                  pack="fa"
                  icon="spinner"
                  custom-class="fa-spin">
                </b-icon>
                <template v-else>
                  {{ props.row.keywordData.allintitle }}
                </template>
                </template>
              </b-table-column>
                <b-table-column numeric field="keywordData.broadMatchMonthlySearches_LowEstimate" label="Monthly searches low" sortable :visible="isColumnVisible('keywordData.broadMatchMonthlySearches_LowEstimate')" :meta="getColumnMeta('keywordData.broadMatchMonthlySearches_LowEstimate')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.broadMatchMonthlySearches_LowEstimate }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.broadMatchMonthlySearches_HighEstimate" label="Monthly searches high" sortable :visible="isColumnVisible('keywordData.broadMatchMonthlySearches_HighEstimate')" :meta="getColumnMeta('keywordData.broadMatchMonthlySearches_HighEstimate')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.broadMatchMonthlySearches_HighEstimate }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.ai_search_demand" label="AI Search Demand" sortable :visible="isColumnVisible('keywordData.ai_search_demand')" :meta="getColumnMeta('keywordData.ai_search_demand')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.ai_search_demand }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column numeric field="keywordData.difficultyScore" label="Difficulty Score %" sortable :visible="isColumnVisible('keywordData.difficultyScore')" :meta="getColumnMeta('keywordData.difficultyScore')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.difficultyScore }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column centered field="keywordData.currentRankGoogle" label="Current Position (Live Data)" sortable :visible="isColumnVisible('keywordData.currentRankGoogle')" :meta="getColumnMeta('keywordData.currentRankGoogle')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.currentRankGoogle }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column centered field="keywordData.competitionStrength" label="Competition strength" sortable :visible="isColumnVisible('keywordData.competitionStrength')" :meta="getColumnMeta('keywordData.competitionStrength')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.competitionStrength }}
                  </template>
                  </template>
                </b-table-column>
                <b-table-column centered field="keywordData.estimatedTimeToWin_in_Months" label="Est. Months to Win" sortable :visible="isColumnVisible('keywordData.estimatedTimeToWin_in_Months')" :meta="getColumnMeta('keywordData.estimatedTimeToWin_in_Months')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <b-icon v-if="props.row.keywordResearchDataTaskId"
                    pack="fa"
                    icon="spinner"
                    custom-class="fa-spin">
                  </b-icon>
                  <template v-else>
                    {{ props.row.keywordData.estimatedTimeToWin_in_Months }}
                  </template>
                  </template>
                </b-table-column>

                <b-table-column field="currentRankingUrl" label="Current Ranking URL (Live Data)" sortable class="url-cell" sticky :visible="isColumnVisible('currentRankingUrl')" :meta="getColumnMeta('currentRankingUrl')">
                  <div v-if="!props.row.__isKeywordTypeGroupHeader">
                    <a
                      v-if="props.row.keywordData.currentRankingUrl"
                      target="_blank"
                      :href="props.row.keywordData.currentRankingUrl"
                      :title="props.row.keywordData.currentRankingUrl"
                    >
                      {{ props.row.keywordData.currentRankingUrl }}
                    </a>
                  </div>
                </b-table-column>

                <b-table-column v-if="silo?.pageType === 'existing' || !isWhiteGloveUser" field="page" label="Avg Ranking URL (SC)" sortable class="url-cell" sticky :meta="getColumnMeta('page')" :visible="isColumnVisible('page')">
                  <div v-if="!props.row.__isKeywordTypeGroupHeader">
                    <a
                      v-if="props.row.keywordData.page"
                      target="_blank"
                      :href="props.row.keywordData.page"
                      :title="props.row.keywordData.page"
                    >
                      {{ props.row.keywordData.page }}
                    </a>
                  </div>
                </b-table-column>
                
                <b-table-column
                  centered
                  field="pageRunResult.pageRunScore"
                  label="POP score"
                  sortable
                  :visible="isColumnVisible('pageRunResult.pageRunScore')"
                  :meta="getColumnMeta('pageRunResult.pageRunScore')"
                >
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template v-if="props.row.pageRunId && props.row?.pageRunResult?.status === 'SUCCESS' && !props.row.pageRunResult.pageNotBuilt">
                    <span
                      class="flex align-sm-center justify-md-center cursor-pointer"
                      @click="gotoPage(`/sites/${props.row.siteId}/pages/${props.row.pageId}/pageruns/${props.row.pageRunId}`)"
                    >
                      <radial-progress-bar
                        :diameter="35"
                        :completed-steps="props.row.pageRunResult.pageRunScore"
                        :total-steps="100"
                        :innerStrokeWidth="6"
                        :strokeWidth="4"
                        :startColor="pageScoreTintColor(props.row.pageRunResult.pageRunScore)"
                        :stopColor="pageScoreTintColor(props.row.pageRunResult.pageRunScore)"
                        :innerStrokeColor="'#D9D9D9'"
                        strokeLinecap="round"
                      />
                      <span class="ml-2">{{ props.row.pageRunResult.pageRunScore }}</span>
                    </span>
                  </template>
                  <span
                    v-else-if="props.row.pageRunId && props.row?.pageRunResult?.status === 'SUCCESS'"
                    class="cursor-pointer"
                    @click="gotoPage(`/sites/${props.row.siteId}/pages/${props.row.pageId}/pageruns/${props.row.pageRunId}`)"
                  >
                    N/A
                  </span>
                  </template>
                </b-table-column>

                <b-table-column field="pageRunResult.url" label="POP report URL" sortable class="url-cell" sticky :meta="getColumnMeta('pageRunResult.url')" :visible="isColumnVisible('pageRunResult.url')">
                  <template v-if="!props.row.__isKeywordTypeGroupHeader">
                  <template v-if="props.row.pageRunId && props.row?.pageRunResult?.status === 'SUCCESS' && !props.row.pageRunResult.pageNotBuilt">
                    <a
                      v-if="props.row?.pageRunResult?.url"
                      target="_blank"
                      :href="props.row?.pageRunResult?.url"
                      :title="props.row?.pageRunResult?.url"
                    >
                      {{ props.row?.pageRunResult?.url }}
                    </a>
                  </template>
                  <span
                    v-else-if="props.row.pageRunId && props.row?.pageRunResult?.status === 'SUCCESS'"
                    class="cursor-pointer"
                    @click="gotoPage(`/sites/${props.row.siteId}/pages/${props.row.pageId}/pageruns/${props.row.pageRunId}`)"
                  >
                    N/A
                  </span>
                  </template>
                </b-table-column>
          
                <!-- Page category -->
                <!------------------->

                <b-table-column
                  v-if="isWhiteGloveUser"
                  :visible="!isDeletedView"
                  field="page_category" label="Page category"
                >
                  <b-dropdown
                    v-if="!props.row.__isKeywordTypeGroupHeader"
                    aria-role="list" class="page-category-dropdown"
                  >
                    <template #trigger>
                      <b-button
                        class="page-category-dropdown-button"
                        type="mainColor--text"
                        :label="
                          AVAILABLE_PAGE_CATEGORIES.filter(option => option.key === props.row.pageCategory)?.[0]?.label || props.row.pageCategory || 'Select'
                        "
                        icon-right="angle-down" />
                    </template>

                    <b-dropdown-item
                      v-for="option in AVAILABLE_PAGE_CATEGORIES" :key="option.key"
                      aria-role="listitem"
                      :disabled="props.row.pageCategory === option.key"
                      @click="updatePageCategory(props.row.id, option.key)"
                    >
                      <b-icon
                        :icon="props.row.pageCategory === option.key?'check':''"
                        type="is-success"
                        size="is-small"
                      ></b-icon>
                      {{ option.label }}
                    </b-dropdown-item>
                  </b-dropdown>
                </b-table-column>

                <!-- Actions -->
                <!------------->
      
                <b-table-column field="action" label="Actions">
                  <div class="d-flex align-center gap10">
                    <template v-if="!props.row.__isKeywordTypeGroupHeader">
                      <template v-if="!isDeletedView">
                        <template v-if="!isWhiteGloveUser || (isWhiteGloveUser && isMissionControlSession)">
                          <v-menu
                            v-if="props.row.keywordData.isTopLevelKeyword && props.row.pageRunId"
                            class="actions-menu"
                            offset-y
                            top
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                text
                                small
                                color="profileAnchorColor"
                                :ripple="false"
                                class="text-none"
                                v-bind="attrs"
                                v-on="on"
                                :disabled="Boolean(POPReportTaskIntervals[props.row.id])"
                              >
                                <radial-progress-bar
                                  v-if="Boolean(POPReportTaskIntervals[props.row.id])"
                                  style="margin-left: -8px;"
                                  :diameter="30"
                                  :completed-steps="POPReportTaskIntervals[props.row.id]?.status?.value"
                                  :total-steps="100"
                                  :strokeWidth="4"
                                  startColor="#00AA64"
                                  stopColor="#00AA64"
                                  :innerStrokeColor="'#eee'"
                                  strokeLinecap="round"
                                />
                                <i v-else class="material-icons">speed</i>
                                <span class="ml-1 font-weight-bold">POP report</span>
                              </v-btn>
                            </template>
                            <v-card class="silo-report-menu">
                              <div style="padding-top: 15px;">
                                <div class="has-text-weight-bold" style="padding-left: 15px;">
                                  Generate POP report
                                </div>
                                <div
                                  style="padding-top: 10px; padding-left: 15px; padding-right: 15px; padding-bottom: 10px;"
                                  class="cursor-pointer pop-report-menu-item"
                                  @click="gotoNewPopReport(props.row)"
                                >
                                  POP Content Brief report
                                </div>
                                <div
                                  v-if="props.row.keywordData.isTopLevelKeyword && !props.row?.pageRunResult?.pageNotBuilt"
                                  style="padding-top: 10px; padding-left: 15px; padding-right: 15px; padding-bottom: 10px;"
                                  class="cursor-pointer pop-report-menu-item"
                                  @click="gotoPageRuns(props.row)"
                                >
                                  SEO Boost Checklist
                                </div>
                                <div
                                  v-if="!isWhiteGloveUser"
                                  style="padding-top: 10px; padding-left: 15px; padding-right: 15px; padding-bottom: 10px;"
                                  class="cursor-pointer pop-report-menu-item"
                                  @click="gotoAiRun(props.row.pageRunId)"
                                >
                                  Go to AI Writer
                                </div>
                              </div>
                            </v-card>
                          </v-menu>
                          <v-menu
                            v-else
                            class="actions-menu"
                            offset-y
                            top
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                text
                                small
                                color="profileAnchorColor"
                                :ripple="false"
                                class="text-none"
                                v-bind="attrs"
                                v-on="on"
                                :disabled="Boolean(POPReportTaskIntervals[props.row.id])"
                              >
                                <radial-progress-bar
                                  v-if="Boolean(POPReportTaskIntervals[props.row.id])"
                                  style="margin-left: -8px;"
                                  :diameter="30"
                                  :completed-steps="POPReportTaskIntervals[props.row.id]?.status?.value"
                                  :total-steps="100"
                                  :strokeWidth="4"
                                  startColor="#00AA64"
                                  stopColor="#00AA64"
                                  :innerStrokeColor="'#eee'"
                                  strokeLinecap="round"
                                />
                                <i v-else class="material-icons">speed</i>
                                <span class="ml-1 font-weight-bold">POP report</span>
                              </v-btn>
                            </template>
                            <v-card class="silo-report-menu">
                              <div>
                                <div
                                  style="padding-top: 10px; padding-left: 15px; padding-right: 15px; padding-bottom: 10px;"
                                  class="cursor-pointer pop-report-menu-item"
                                  @click="gotoNewPopReport(props.row)"
                                >
                                  Generate POP report
                                </div>
                                <div
                                  v-if="!isWhiteGloveUser && props.row.pageRunId"
                                  style="padding-top: 10px; padding-left: 15px; padding-right: 15px; padding-bottom: 10px;"
                                  class="cursor-pointer pop-report-menu-item"
                                  @click="gotoAiRun(props.row.pageRunId)"
                                >
                                  Go to AI Writer
                                </div>
                              </div>
                            </v-card>
                          </v-menu>
                        </template>

                        <template
                          v-if="(
                            (props.row.pageRunId && props.row?.pageRunResult?.status === 'SUCCESS')
                            && (
                              silo?.content_report_order?.fulfillement_status === 'completed'
                              || (
                                silo?.content_report_order?.fulfillement_status !== 'completed' && isMissionControlSession
                              )
                            )
                          )"
                        >
                          <v-btn
                            v-if="selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0"
                            text
                            small
                            color="profileAnchorColor"
                            :ripple="false"
                            class="text-none"
                            @click="handleViewContent(props.row, silo?.content_report_order)"
                          >
                            <span class="font-weight-bold">View content</span>
                          </v-btn>
                          <silo-action-menu
                            :ref="'silo-action-menu-' + props.row.id"
                            :siloKeyword="props.row"
                            :silo="silo"
                            :isWhiteGloveUser="isWhiteGloveUser"
                            :isMissionControlSession="isMissionControlSession"
                            :isOrderedReport="['to_be_paid_by_invoice', 'paid'].includes(silo?.content_report_order?.order_status) && selectedKeywordIdsForOrder.indexOf(props.row.id) >= 0"
                            :orderedReportType="getContentReportTypeId(props.row)"
                            @refreshData="refreshData"
                          />
  
                        </template>
                        </template>
                      <v-btn
                        v-else
                        text
                        small
                        color="profileAnchorColor"
                        :ripple="false"
                        class="text-none"
                        :disabled="keywordsOngoingRemoval.indexOf(props.row.id) >= 0"
                        @click="restoreKeyword(props.row.id)"
                      >
                        <i class="material-icons">replay</i>
                        <span class="ml-1">Recover</span>
                      </v-btn>
                    </template>
                  </div>
                </b-table-column>
              </template>
            </b-table>
            </div>
          </horizontal-two-scroll-bar>

          <content-order-form
            v-if="isWhiteGloveUser && showContentOrderFormModal"
            :isQuickContentOrderView="isQuickContentOrderView"
            :isEditMode="isContentOrderEditable"
            :initialOrder="silo?.content_report_order"
            :initialKeywords="selectedKeywordsForOrder"
            :refreshData="refreshData"
            :projectTitle="site?.name"
            @close="showContentOrderFormModal = false"
          />

        </template>
        <template v-else>
          <div>
            <!-- No data to display -->
          </div>
        </template>
        <div
          :style="showGraphChart?'':'position:fixed; top: 200%; left: 200%;'"
        >
          <silo-result-table-graph
            :graph-data="graphData"
            :key="graphDataRepr"
          />
        </div>
      </div>

      <div v-if="!isCalculating" class="mt-4 flex" style="justify-content: flex-end;">
        <div v-if="showAddToNewTrackListModalFor" class="modal search-keywords-cost-modal" :class="{'is-active': true}">
          <div class="modal-background" @click.stop="showAddToNewTrackListModalFor = null;"></div>
          <div class="modal-content">
            <div class="box modal-container">
              <div class="modal-content-root">
                <p class="title-font is-size-4 mainColor3--text mb-8">Add "{{showAddToNewTrackListModalFor.keyword}}" to a new tracking list</p>

                <div>
                  <div
                    class="mb-2"
                    style="font-size: 0.9rem; font-weight: 700; color: var(--v-mainColor3-base);"
                  >
                    Name
                  </div>

                  <v-text-field
                    v-model="newListFieldValue"
                    :autofocus="true"
                    placeholder="Enter list name"
                    hide-details
                    outlined
                    dense
                    color="grayColor"
                  />
                </div>

                <div class="d-flex justify-end gap10 mt-8">
                  <v-btn
                    outlined color="grayColor" class="text-none"
                    @click="showAddToNewTrackListModalFor = null;"
                  >
                    <span style="color: var(--v-mainColor3-base);">Cancel</span>
                  </v-btn>
                  <v-btn color="profileAnchorColor"
                    :disabled="!newListFieldValue || trackKeywordLists.map(name => name.toLowerCase()).includes(newListFieldValue.toLowerCase())"
                    class="whiteColor--text text-none"
                    @click.stop="toggleTrackUntrackKeyword(true, showAddToNewTrackListModalFor, listName=newListFieldValue)">
                    <span style="font-weight: 700;">Save</span>
                  </v-btn>
                  
                </div>
              </div>
            </div>
            <button
              class="custom-modal-close-btn"
              aria-label="close"
              @click="showAddToNewTrackListModalFor = null;">
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { jsPDF } from "jspdf";
import moment from 'moment';
import * as htmlToImage from 'html-to-image';

import SiloActionMenu from '@/components/Campaigns/silo/SiloActionMenu.vue';
import { filter, map } from 'lodash';
import RadialProgressBar from 'vue-radial-progress';
import ContentOrderForm from '@/components/Campaigns/content-orders/ContentOrderForm/Index.vue'
import HorizontalTwoScrollBar from '@/common/HorizontalTwoScrollBar.vue';
import { pageScoreTintColor, getPureServicePkgName } from '@/utils';
import { getReportHeaderImage } from '@/utils/download-reports';
import { isEmpty } from 'lodash'
import { ReportTypes } from '@/components/PageRun/constants'
import {
  whiteGlovePlans,
} from "@/components/plans_constants"
import SiloResultTableGraph from './SiloResultTableGraph.vue';
import SiloResultTableControls from './SiloResultTableControls.vue';
import {
  popAuthorityCycleAnchorAt,
  popAuthorityCycleMonthName,
  popAuthorityPackageFromStatus,
} from '@/utils/popAuthority'


const HIDDEN_COLUMNS_KEYS_DEFAULT = [
  'keywordData.pageOpportunityScore',
  'keywordData.opportunityScore',
  'keywordData.monthlySearch',
  'keywordData.clicks',
  'keywordData.impressions',
  'keywordData.impressionsShare',
  'keywordData.ctr',
  'keywordData.position',
  'keywordData.competitionStrength',
  'keywordData.estimatedTimeToWin_in_Months',
]

const HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_NEW_CONTENT = [
  'keywordData.pageOpportunityScore',
  'keywordData.opportunityScore',
  'keywordData.monthlySearch',
  'keywordData.clicks',
  'keywordData.impressions',
  'keywordData.impressionsShare',
  'keywordData.ctr',
  'keywordData.position',
  'keywordData.ai_search_demand',
  'keywordData.difficultyScore',
  'keywordData.estimatedTimeToWin_in_Months',
  'keywordData.realCompMajesticTitleAndAnchorComp',
  'keywordData.allintitle',
]

const HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_EXISTING_CONTENT = [
  'keywordData.pageOpportunityScore',
  'keywordData.opportunityScore',
  'keywordData.monthlySearch',
  'keywordData.competitionStrength',
  'keywordData.ai_search_demand',
  'keywordData.difficultyScore',
  'keywordData.estimatedTimeToWin_in_Months',
  'keywordData.realCompMajesticTitleAndAnchorComp',
  'keywordData.allintitle',
  'keywordData.currentRankGoogle'
]


export default {
  inject: {
    // POP Authority layout provides the site's status row (effective package for the selection cap)
    getPopAuthoritySiteStatus: { default: null },
  },
  props: {
    isQuickContentOrderView: {
      default: false,
    },
    isCalculating: {
      default: false,
    },
    silo_keywords: {
      default: [],
    },
    deleted_silo_keywords: {
      default: [],
    },
    siteId: {
      
    },
    silo: {
      
    },
    site: {

    },
    refreshData: {

    },
    allOrderedSiloKeywords: {
      default: [],
    },
    orderContentForSelectedKeywords: {
      type: Function,
      default: null,
    },
    selectedSiloKeywordsRows: {
      type: Array,
      default: null,
    },
  },
  components: {
    SiloActionMenu,
    RadialProgressBar,
    ContentOrderForm,
    HorizontalTwoScrollBar,
    SiloResultTableGraph,
    SiloResultTableControls,
  },
  data() {
    return {
      searchTerm: '',
      showAdvancedMetrics: false,
      showGraphChart: false,
      keywordsOngoingRemoval: [],
      showContentOrderFormModal: false,
      editedKeywordNotes: [],
      sortKey: "keywordType",
      sortOrder: "desc",
      keywordTrackData: null,
      currentIdx: null,
      keywordTaskIntervals: {},
      POPReportTaskIntervals: {},
      isDeletedView: false,
      contentReportPackages: [],
      keywordsOngoingLabelUpdate: [],
      showAddToNewTrackListModalFor: null,
      newListFieldValue: '',
    };
  },
  watch: {
    isMissionControlSession: {
      immediate: true,
      handler (val) {
        if (val) this.showAdvancedMetrics = true
      },
    },
    silo: function () {
      this.editedKeywordNotes = []
    },
    // POP Pilot (Rocky) deep-links to the Silo view with ?view=diagram on the
    // silo route (/sites/:siteId/campaign/:campaignId/silo?id=<siloId>&view=diagram).
    // The toggle itself stays component state, so only the query is read:
    // 'diagram' selects the Silo view, 'table' the keyword table, anything
    // else leaves the current choice alone.
    '$route.query.view': {
      immediate: true,
      handler (view) {
        if (view === 'diagram') this.showGraphChart = true
        else if (view === 'table') this.showGraphChart = false
      },
    },
    silo_keywords: {
      immediate: true,  // means we refresh on mount as well
      handler:function () {
        this.refreshKeywordTaskMonitors()
        this.refreshPOPReportTaskMonitors()
      }
    },
  },
  updated () {
    this.mergeSiloKeywordTypeGroupHeaderCells()
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    isContentOrderEditable () {
      // POP Authority: a content plan whose month was billed is locked (selection + keyword types)
      if (this.isPopAuthorityPlanView && this.paSelectionLocked) return false;
      return [undefined, 'user_editing', 'in_cart'].includes(this.silo?.content_report_order?.order_status);
    },
    isPopAuthorityUser () {
      return Boolean(this.$store.getters.isPopAuthorityUser);
    },
    /** POP Authority content plan (not a content-only order): package quota instead of ordering. */
    isPopAuthorityPlanView () {
      return this.isPopAuthorityUser && !this.isQuickContentOrderView && !this.silo?.isForQuickContentOrder;
    },
    paStatus () {
      return typeof this.getPopAuthoritySiteStatus === 'function' ? this.getPopAuthoritySiteStatus() : null;
    },
    paPackage () {
      return popAuthorityPackageFromStatus(this.paStatus);
    },
    paCycleAnchorAt () {
      return popAuthorityCycleAnchorAt(this.paStatus);
    },
    paSupportingCap () {
      return Number(this.paPackage.supporting_per_silo) || 0;
    },
    paSelectionLocked () {
      return Boolean(this.silo?.paScheduleLockedAt)
        || ['to_be_paid_by_invoice', 'paid'].includes(this.silo?.content_report_order?.order_status);
    },
    paCycleLabel () {
      const n = Number(this.silo?.paScheduledCycle);
      return Number.isFinite(n) && n >= 1 ? popAuthorityCycleMonthName(n, this.paCycleAnchorAt) : 'Not scheduled';
    },
    isContentOrderInCheckout () {
      return this.silo?.content_report_order?.order_status === 'in_checkout';
    },
    isContentOrderInCart () {
      return ['in_cart', 'in_cart_checkout'].includes(this.silo?.content_report_order?.order_status);
    },
    selectedKeywordIdsForOrder() {
      return [
        ...this.silo?.content_report_order?.selected_top_level_keyword_ids || [],
        ...this.silo?.content_report_order?.selected_supporting_keyword_ids || []
      ]
    },
    selectedKeywordsForOrder () {
      return this.sortedTableData.filter(keyword => this.selectedKeywordIdsForOrder.indexOf(keyword.id) >= 0)
    },
    primaryTopLevelKeywords () {
      return this.sortedTableData.filter(keyword => keyword.keywordData.isTopLevelKeyword && keyword.keywordData.isPrimaryKeyword)
    },
    secondaryTopLevelKeywords () {
      return this.sortedTableData.filter(keyword => keyword.keywordData.isTopLevelKeyword && (keyword.keywordData.isSecondaryKeyword || !keyword.keywordData.isPrimaryKeyword))
    },
    isSelectionDisabled() {
      return !!this.siloReportProgress;
    },
    AVAILABLE_PAGE_CATEGORIES() {
      return [
        {key: 'product', label: 'Product'},
        {key: 'service', label: 'Service'},
        {key: 'informational', label: 'Informational'},
        {key: 'category', label: 'Category'},
        {key: 'supporting', label: 'Supporting'},
      ]
    },
    filteredSilos() {
      return this.searchTerm ? this.silo_keywords.filter(silo_keyword => silo_keyword.keyword?.toLowerCase().includes(this.searchTerm?.toLowerCase())) : this.silo_keywords
    },
    selectedSiloKeywords() {
      return this.$store.getters.selectedSiloKeywords;
    },
    campaignId() {
      return parseInt(this.$route.params.campaignId || 0);
    },
    config() {
      return this.$store.getters.insightKeywordsConfig;
    },
    /** Same rows as sortedTableData before sort — avoids circular deps with siloKeywordTypeGroupTier */
    siloTableKeywordsPreSort() {
      const searchTerm = this.searchTerm ? this.searchTerm.toLowerCase() : ''
      return [...this.filteredSilos].filter((item) => {
        if (!searchTerm) return true
        return item.keyword.includes(searchTerm) || item.page.includes(searchTerm)
      })
    },
    sortedTableData() {
      let data = [...this.siloTableKeywordsPreSort]

      if (this.sortKey) {
        let modifier = this.sortOrder === "desc" ? -1 : 1;

        if (this.sortKey === "keywordType") {
          const typeGroupRank = (row) => {
            const t = this.siloKeywordTypeGroupTier(row)
            const map = { 5: 0, 4: 1, 2: 2, 1: 3 }
            return map[t] ?? 99
          }
          data.sort((a, b) => {
            let cmp = typeGroupRank(a) - typeGroupRank(b)
            if (cmp !== 0) return cmp

            const aOrdered = this.selectedKeywordIdsForOrder.indexOf(a.id) >= 0 ? 1 : 0
            const bOrdered = this.selectedKeywordIdsForOrder.indexOf(b.id) >= 0 ? 1 : 0
            if (aOrdered !== bOrdered) {
              return bOrdered - aOrdered
            }

            let aKGRScore = a.keywordData.KGRScore || 0
            let bKGRScore = b.keywordData.KGRScore || 0
            if (bKGRScore !== aKGRScore) {
              return (aKGRScore - bKGRScore) * modifier * -1
            }

            return (a.id - b.id) * modifier
          });
        }

        else if (this.sortKey === "order_reports") {
          const orderRank = (row) => (this.selectedKeywordIdsForOrder.indexOf(row.id) >= 0 ? 1 : 0)
          data.sort((a, b) => {
            const ra = orderRank(a)
            const rb = orderRank(b)
            if (ra !== rb) {
              return this.sortOrder === "desc" ? rb - ra : ra - rb
            }
            return (a.id - b.id) * modifier
          });
        }

        else if (this.sortKey === "pageRunResult.pageRunScore") {
          data.sort((a, b) => {
            let aValue = a["pageRunResult"]["pageRunScore"] ?? "";
            let bValue = b["pageRunResult"]["pageRunScore"] ?? "";
            if (aValue < bValue) return -1 * modifier;
            if (aValue > bValue) return 1 * modifier;

            // fallback to sort by id
            return (a.id - b.id) * modifier
          });
        }

        else {
          data.sort((a, b) => {
            const keys = this.sortKey.split('.', 2);

            let aValue = a?.[keys?.[0]]?.[keys?.[1]] ?? 0;
            let bValue = b?.[keys?.[0]]?.[keys?.[1]]  ?? 0;

            if (aValue !== bValue) {
              return (aValue - bValue) * modifier;
            }

            // fallback to sort by id
            return (a.id - b.id) * modifier
          });
        }
      }

      return data;
    },
    sortedTableDataWithKeywordTypeGroupRows() {
      const data = this.sortedTableData
      if (this.sortKey !== 'keywordType') {
        return data
      }
      const out = []
      let sectionIdx = 0
      let i = 0
      while (i < data.length) {
        const tier = this.siloKeywordTypeGroupTier(data[i])
        let j = i + 1
        while (j < data.length && this.siloKeywordTypeGroupTier(data[j]) === tier) {
          j += 1
        }
        const chunk = data.slice(i, j)
        out.push({
          __isKeywordTypeGroupHeader: true,
          id: `__kwg_${tier}_${sectionIdx}`,
          __groupLabel: this.siloKeywordTypeGroupLabelForTier(tier),
          __groupCount: chunk.length
        })
        chunk.forEach((row) => out.push(row))
        sectionIdx += 1
        i = j
      }
      return out
    },
    isAllSelected() {
      return this.selectedSiloKeywords.length === this.silo_keywords.length
    },
    tableColumnsAll() {
      return [
          {
            key: 'keyword',
            label: 'Keyword',
            value: 'keyword',            
          },
          {
            key: 'keywordType',
            label: 'Type',
            tooltipHeading: "Keyword Type",
            tooltipText: "Indicates whether a keyword is a <b>primary focus (Top Level)</b> for a content silo or a <b>related term (Supporting Keyword)</b> that helps reinforce the topic.",
            downloadButtonValue: (row) => {
              if (row.keywordData.isTopLevelKeyword) {
                return 'Top-Level Keyword'
              }
              if (row.keywordData.isSupportingKeyword) {
                return 'Supporting Keyword'
              }
              return '';
            },
          },

          /* --- VoltageTable columns --- */
          /* ---------------------------- */
          ...this?.silo?.pageType === 'existing'
            ?[
              {
                isAdvancedMetric: true,
                key: 'keywordData.pageOpportunityScore',
                label: 'Page Opportunity Score',
                tooltipHeading: 'Page Opportunity Score (POS)',
                tooltipText: 'A score that helps identify pages with the best potential for growth. It considers <b>CTR, clicks, average position, and ranking keywords</b> to highlight opportunities. Even if impressions or keywords are low, a high POS means the page performs well in key areas and may benefit from further optimization.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.pageOpportunityScore !== undefined) {
                    return row.keywordData.pageOpportunityScore;
                  }
                  return ''
                },
              },
              {
                isAdvancedMetric: true,
                key: 'keywordData.opportunityScore',
                label: 'Keyword Opportunity Score',
                tooltipHeading: 'Search Console Keyword Score',
                tooltipText: 'A metric based on <b>Google Search Console data</b>, reflecting <b>real-world performance</b> using existing keyword rankings. It helps prioritize opportunities for optimization based on actual user searches and site visibility.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.opportunityScore !== undefined) {
                    return row.keywordData.opportunityScore;
                  }
                  return ''
                },
              },
              // Opportunity Level only shown if at least one row comes from KeywordWizardResearch
              ...this.sortedTableData.some(row => row.keywordData.opportunityScore !== undefined)
                ?[{
                  isAdvancedMetric: true,
                  key: 'keywordData.isBestOpportunityKeyword',
                  label: 'Opportunity Level',
                  downloadButtonValue: (row) => {
                    if (row.keywordData.isBestOpportunityKeyword !== undefined) {
                      if (row.keywordData.isBestOpportunityKeyword) {
                        return 'Best'
                      }
                    }
                    return ''
                  },
                }]
                :[]
              ,
              {
                isAdvancedMetric: true,
                key: 'keywordData.monthlySearch',
                label: 'Search volume',
                tooltipText: 'The estimated <b>number of searches per month</b> for a given keyword. This helps assess the potential traffic opportunity if ranking for that term.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.monthlySearch !== undefined) {
                    return row.keywordData.monthlySearch;
                  }
                  return ''
                },
              },

              {
                isAdvancedMetric: true,
                key: 'keywordData.impressionsShare',
                label: 'Search impressions share %',
                tooltipText: 'The <b>percentage of total available impressions</b> a site receives for a keyword. A lower percentage suggests room for growth in rankings and visibility.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.impressionsShare !== undefined) {
                    return row.keywordData.impressionsShare;
                  }
                  return ''
                },
              },
              {
                isAdvancedMetric: true,
                key: 'keywordData.impressions',
                label: 'Impressions',
                tooltipHeading: 'Impressions (Search Console data)',
                tooltipText: 'The number of times a page appears in Google Search results. Each time a user sees the page in search, it counts as an impression, regardless of whether they click on it.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.impressions !== undefined) {
                    return row.keywordData.impressions;
                  }
                  return ''
                },
              },
              {
                isAdvancedMetric: true,
                key: 'keywordData.clicks',
                label: 'Clicks',
                tooltipHeading: 'Clicks (Search Console data)',
                tooltipText: 'The number of times users click on a page from Google Search results. A click means a user actively chose to visit the page after seeing it in the search results.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.clicks !== undefined) {
                    return row.keywordData.clicks;
                  }
                  return ''
                },
              },
              {
                isAdvancedMetric: true,
                key: 'keywordData.ctr',
                label: 'CTR',
                tooltipHeading: 'CTR (Search Console data)',
                tooltipText: 'Click-through rate (CTR) is the percentage of users who click on a search result after seeing it. It is calculated as: <b>CTR = (Clicks / Impressions) × 100</b> A high CTR often indicates compelling title tags and meta descriptions.',
                downloadButtonValue: (row) => {
                  if (row.keywordData.ctr !== undefined) {
                    return row.keywordData.ctr;
                  }
                  return ''
                },
              },
              {
                isAdvancedMetric: true,
                key: 'keywordData.position',
                label: 'Avg Position (SC)',
                tooltipHeading: 'Avg Position (Search Console data)',
                tooltipText: 'The average ranking of a page in Google Search results for the keywords it appears for, calculated over the selected period. Lower numbers indicate higher rankings (e.g., a position of <b>1</b> means the page is the top result).',
                downloadButtonValue: (row) => {
                  if (row.keywordData.position !== undefined) {
                    return row.keywordData.position;
                  }
                  return ''
                },
              },
            ]:[],

          /* --- KeywordResearchResult columns --- */
          /* ------------------------------------- */
          
          {
            key: 'keywordData.keyword_intent',
            label: 'Intent',
            tooltipHeading: 'Keyword Intent',
            tooltipText: 'This shows what the person is trying to do when they search a keyword, whether they’re just looking for info, trying to visit a site, ready to take action (like buy something), or comparing options before a decision.',
            downloadButtonValue: (row) => {
              if (row.keywordData.keyword_intent !== undefined) {
                return row.keywordData.keyword_intent;
              }
            },
          },
          {
            key: 'keywordData.keywordScore',
            label: 'Keyword score',
            tooltipText: 'The Keyword Score indicates the value of a keyword for your SEO strategy. It combines monthly search volume, competition strength, and keyword difficulty into a single, color-coded score, with darker greens signaling better keyword opportunities.',
            downloadButtonValue: (row) => {
              if (row.keywordData.keywordScore !== undefined) {
                return row.keywordData.keywordScore;
              }
            },
          },
          {
            key: 'keywordData.ta_score',
            label: 'T&A Score',
            tooltipText: "The TA Score measures your website's Trust and Authority on a scale of 1 to 20. We use it to identify keywords Google should immediately trust your site to rank for. In most cases, you can rank easily for keywords with a TA Score at or below your site's score, so a 3/20 site should target keywords rated 3 or under for the quickest wins. Keep in mind that TA Scores apply only to supporting keywords, not top-level keywords. Top-level keywords are driven by necessity (every site in a niche has to have them), so the score doesn't matter there. Supporting keywords are where you have control, and they're what help you build authority for the top-level terms you're required to rank for.",
            downloadButtonValue: (row) => {
              if (row.keywordData.ta_score !== undefined && row.keywordData.ta_score !== undefined) {
                return row.keywordData.ta_score;
              }
            },
          },
          {
            key: 'keywordData.KGRScore',
            label: 'KGR Score',
            tooltipText: "The Keyword Golden Ratio metric identifies low-competition, high-opportunity keywords by calculating the ratio of Google search results to local monthly search volume. A score below 2.5 suggests a strong chance to rank in the top 50 results.",
            isAdvancedMetric: true,
            downloadButtonValue: (row) => {
              if (row.keywordData.KGRScore !== undefined) {
                return row.keywordData.KGRScore;
              }
            },
          },
          {
            isAdvancedMetric: true,
            key: 'keywordData.realCompMajesticTitleAndAnchorComp',
            label: 'SEO Competitiveness Index (SCI)',
            tooltipHeading: "SEO Competitiveness Index (SCI)",
            tooltipText: "The SEO Competitiveness Index score evaluates how competitive a keyword is based on the frequency of title optimization and the presence of relevant anchor text in backlinks. Higher SCI indicates more competitors actively optimizing for this keyword.",
            downloadButtonValue: (row) => {
              if (row.keywordData.realCompMajesticTitleAndAnchorComp !== undefined) {
                return row.keywordData.realCompMajesticTitleAndAnchorComp;
              }
            },
          },
          {
            key: 'keywordData.allintitle',
            label: 'All In Title',
            isAdvancedMetric: true,
            tooltipText: "This metric shows the number of pages that use the exact keyword in their title tag. A higher number suggests more competition targeting the keyword in on-page SEO.",
            downloadButtonValue: (row) => {
              if (row.keywordData.allintitle !== undefined) {
                return row.keywordData.allintitle;
              }
            },
          },
          {
            key: 'keywordData.broadMatchMonthlySearches_LowEstimate',
            label: 'Monthly searches low',
            tooltipText: 'This represents the lowest estimated monthly search volume for a keyword, helping you gauge its baseline popularity.',
            downloadButtonValue: (row) => {
              if (row.keywordData.broadMatchMonthlySearches_LowEstimate !== undefined) {
                return row.keywordData.broadMatchMonthlySearches_LowEstimate;
              }
            },
          },
          {
            key: 'keywordData.broadMatchMonthlySearches_HighEstimate',
            label: 'Monthly searches high',
            tooltipText: 'This represents the highest estimated monthly search volume for a keyword, indicating peak search demand.',
            downloadButtonValue: (row) => {
              if (row.keywordData.keywordScore !== undefined) {
                return row.keywordData.broadMatchMonthlySearches_HighEstimate;
              }
            },
          },
          {
            isAdvancedMetric: true,
            key: 'keywordData.ai_search_demand',
            label: 'AI Search Demand',
            tooltipHeading: 'AI Search Demand (Search Volume in AI tools)',
            tooltipText: 'This shows <b>how many people search for a topic</b>, on average, in a selected country/language.<ul style="list-style-type: initial; margin-bottom: 15px;"><li><b>What it answers:</b> “Is this something people actually look for?”</li><li><b>What you get:</b> an estimated <b>monthly search count</b> for each keyword/topic you track.</li><li><b>Why it matters:</b> it helps you <b>prioritize</b> which topics are worth focusing on (high demand = bigger potential reach).</li><li><b>How to use it:</b> compare topics and pick the ones with the best mix of <b>demand and relevance</b>.</li></ul><b>Example:</b><br/>If you track “best project management tool”, this metric tells you whether it’s searched <b>a lot, a little, or almost never</b>, so you know if it’s worth investing time in.<br/><b>Note:</b> This measures <b>human search interest</b>, not “AI visibility” directly but it helps you choose topics that can drive more traffic when you do get mentioned.',
            downloadButtonValue: (row) => {
              if (row.keywordData.ai_search_demand !== undefined) {
                return row.keywordData.ai_search_demand;
              }
            },
          },
          {
            isAdvancedMetric: true,
            key: 'keywordData.difficultyScore',
            label: 'Difficulty Score %',
            tooltipText: 'Indicates how hard it is to rank in the top 10 search results for a keyword, on a scale of 0 to 100. Higher percentages reflect higher competition and ranking difficulty.',
            downloadButtonValue: (row) => {
              if (row.keywordData.difficultyScore !== undefined) {
                return row.keywordData.difficultyScore;
              }
            },
          },
          {
            key: 'keywordData.currentRankGoogle',
            label: 'Current Position (Live Data)',
            tooltipText: "Your current Google ranking position for this keyword, based on live search results. This reflects the ranking of the Current Ranking URL (Live Data). Lower numbers mean higher positions.",
            downloadButtonValue: (row) => {
              if (row.keywordData.currentRankGoogle !== undefined) {
                return row.keywordData.currentRankGoogle;
              }
            },
          },
          {
            isAdvancedMetric: true,
            key: 'keywordData.competitionStrength',
            label: 'Competition strength',
            tooltipText: 'Estimates the competitive strength of a keyword based on difficulty metrics, helping you assess the effort needed to outrank competitors.',
            downloadButtonValue: (row) => {
              if (row.keywordData.competitionStrength !== undefined) {
                return row.keywordData.competitionStrength;
              }
            },
          },
          {
            key: 'currentRankingUrl',
            label: 'Current Ranking URL (Live Data)',
            tooltipText: 'This is the page currently showing up on Google for the keyword. We check this live right now. It\'s what users are most likely seeing in search results.',
            downloadButtonValue: (row) => {
              return row.keywordData.currentRankingUrl;
            },
          },
          ...this?.silo?.pageType === 'existing'
            ?[
              {
                isAdvancedMetric: true,
                key: 'page',
                label: 'Avg Ranking URL (SC)',
                tooltipText: 'This is the page that ranked most often for the keyword over the past 28 days, based on Google Search Console. It shows what Google usually shows, on average.',
                downloadButtonValue: (row) => {
                  return row.keywordData.page;
                }
              }
            ]:[],
          {
            key: 'pageRunResult.pageRunScore',
            label: 'POP score',
            tooltipText: "The POP Rank Engine™️ evaluates over 300 SEO factors to provide recommendations aimed at improving your on-page SEO. This score reflects the effectiveness of your page optimization based on ongoing SEO experiments.",
            downloadButtonValue: (row) => {
              if (row.pageRunId && !row.pageRunResult.pageNotBuilt) {
                return row.pageRunResult.pageRunScore
              } else if (row.pageRunId) {
                return 'N/A'
              }
              return '';
            },
          },
          {
            key: 'pageRunResult.url',
            label: 'Pop report URL',
            isAdvancedMetric: true,
            tooltipText: 'This is the page we analyzed in the POP report. If it’s different from what’s ranking, you might want to update your SEO focus or check why a different page is ranking.',
            downloadButtonValue: (row) => {
              if (row.pageRunId && !row.pageRunResult.pageNotBuilt) {
                return row.pageRunResult.url
              } else if (row.pageRunId) {
                return 'N/A'
              }
              return '';
            },
          },
          ...this.isAdmin
          ?[
            {
              isAdvancedMetric: true,
              key: 'keywordData.estimatedTimeToWin_in_Months',
              label: 'Est. Months to Win',
              tooltipText: "Reflects the anticipated duration, in months, to achieve a top 10 ranking position for a keyword. Ranges are categorized from a fast track of 2-3 months to longer efforts exceeding 12 months.",
              downloadButtonValue: (row) => {
                if (row.keywordData.estimatedTimeToWin_in_Months !== undefined) {
                  return row.keywordData.estimatedTimeToWin_in_Months;
                }
              },
            }
          ]:[],
        ];
    },
    tableColumns() {
      return this.tableColumnsAll.filter((column) => !(column.isAdvancedMetric && !this.showAdvancedMetrics))
    },
    hiddenColumnsKeys () {
      const settingKey = `${this.siteId}-${this.isWhiteGloveUser ? this?.silo?.pageType : ''}`

      const globalValue = (
        this?.user?.settings?.siloResultTableHiddenColumnsKeysPerSite?.[settingKey]
        || this?.user?.settings?.siloResultTableHiddenColumnsKeysPerSite?.[this.siteId]
      )
      if (globalValue === null || globalValue === undefined) {
        if (this.isWhiteGloveUser) {
          if (this?.silo?.pageType == 'new') {
            return HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_NEW_CONTENT
          }
          if (this?.silo?.pageType == 'existing') {
            return HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_EXISTING_CONTENT
          }
        }
        return HIDDEN_COLUMNS_KEYS_DEFAULT
      }
      const validKeys = new Set(this.tableColumns.map((column) => column.key))
      return Array.isArray(globalValue)
        ? globalValue.filter((key) => validKeys.has(key))
        : globalValue
    },
    hideableColumns () {
      return [
        // all columns
        ...this.tableColumnsAll.filter((column) => [
          // except
          'keyword',
          'keywordType',
          'keywordData.isBestOpportunityKeyword'
        ].indexOf(column.key) < 0),
      ]
    },
    visibleTableColumns () {
      return this.tableColumns.filter(column => {
        return this.isColumnVisible(column.key)
      })
    },
    graphData() {
      const siloKeywords = this.isWhiteGloveUser
        ? this.silo_keywords.filter(k => this.selectedKeywordIdsForOrder.indexOf(k.id) >= 0)
        : [...this.silo_keywords]

      const topLevel = filter(siloKeywords, 'keywordData.isTopLevelKeyword')
      const supporting = filter(siloKeywords, 'keywordData.isSupportingKeyword')

      const mid = Math.ceil(supporting?.length / 2);
      const leftKeywords = supporting?.slice(0, mid) || [];
      const rightKeywords = supporting?.slice(mid) || [];

      const mk = (list) => map(list, 'keyword')
      return { center: topLevel[0]?.keyword, left: mk(leftKeywords), right: mk(rightKeywords) }
    },
    graphDataRepr() {
      return JSON.stringify(this.graphData)
    },
    trackedKeywords() {
      return this?.keywordTrackData?.trackKeywords || []
    },
    trackKeywordLists() {
      return this?.keywordTrackData?.keywordLists?.map(l => l.name) || []
    }
  },
  methods: {
    siloKeywordTypeGroupTier(row) {
      const kd = row?.keywordData
      if (!kd) return 1
      if (kd.isSupportingKeyword) return 2
      if (kd.isTopLevelKeyword) {
        const hasPrimaryTop = this.siloTableKeywordsPreSort.some(
          (k) => k.keywordData?.isTopLevelKeyword && k.keywordData?.isPrimaryKeyword
        )
        const isStl = kd.isSecondaryKeyword || (hasPrimaryTop && !kd.isPrimaryKeyword)
        return isStl ? 4 : 5
      }
      return 1
    },
    siloKeywordTypeGroupLabelForTier(tier) {
      const labels = {
        5: 'Top-level keywords',
        4: 'Secondary top-level keywords',
        2: 'Supporting keywords',
        1: 'Unmarked keywords'
      }
      return labels[tier] || 'Keywords'
    },
    siloKeywordTypeGroupRowClass(row) {
      if (row && row.__isKeywordTypeGroupHeader) return 'keyword-type-group-header'
      // POP Authority: once the month is billed the rows left out fade (design pa-plan.jsx)
      if (row && this.isPopAuthorityPlanView && this.paSelectionLocked && !this.paIsSelected(row)) return 'pa-row--dimmed'
      return ''
    },
    /** POP Authority "Write" column: is the keyword in the package selection? */
    paIsSelected(row) {
      return Boolean(row) && this.selectedKeywordIdsForOrder.indexOf(row.id) >= 0
    },
    togglePaSelection(row) {
      if (!this.isContentOrderEditable) return
      return this.paIsSelected(row)
        ? this.unselectKeywordForContentReportOrder(row)
        : this.selectKeywordForContentReportOrder(row)
    },
    /**
     * Buefy b-table emits one td per column; merge group-header rows so the label cell
     * spans the full table width (real colspan). Re-run after each render because Vue
     * restores the full set of tds on update.
     */
    mergeSiloKeywordTypeGroupHeaderCells() {
      if (this.isDeletedView || this.sortKey !== 'keywordType') {
        return
      }
      this.$nextTick(() => {
        const root = this.$refs['voltage-table']?.$el
        if (!root) return
        const table = root.querySelector('table.table')
        if (!table) return
        table.querySelectorAll('tbody tr.keyword-type-group-header').forEach((tr) => {
          const tds = [...tr.querySelectorAll('td')]
          const labelTd =
            tr.querySelector('td.keyword-cell') ||
            tr.querySelector('.silo-keyword-type-group-header-label')?.closest('td')
          if (!labelTd || tds.length <= 1) return
          labelTd.colSpan = tds.length
          tds.forEach((td) => {
            if (td !== labelTd) {
              tr.removeChild(td)
            }
          })
        })
      })
    },
    getColumnMeta(key) {
      return this.tableColumnsAll.filter(column => column.key === key)?.[0]
    },
    isActionMenuVisible(siloKeyword) {
      const hasGuardDiffData = siloKeyword.hasGuardDiffData ?? !isEmpty(siloKeyword.guardDiffData)
      return (siloKeyword.keywordData.isTopLevelKeyword && (hasGuardDiffData || siloKeyword.aiRunData)) ||
        (siloKeyword.keywordData.isSupportingKeyword && siloKeyword.aiRunData)
    },
    isValidKeywordSelection(selectedKeywordsForOrder) {
      const topLevelKeywordsCount = selectedKeywordsForOrder.filter(keyword => keyword.keywordData.isTopLevelKeyword).length
      const supportingKeywordsCount = selectedKeywordsForOrder.filter(keyword => keyword.keywordData.isSupportingKeyword).length
      return (
        selectedKeywordsForOrder.filter(keyword => this.allOrderedSiloKeywords.includes(keyword.keyword)).length === 0
        && topLevelKeywordsCount <= 1
        && (topLevelKeywordsCount + supportingKeywordsCount > 0)
      )
    },
    scoreTierToColorStyles(tier) {
      switch (tier) {
        case 0:
          return {'background-color': '#FFD989', 'color': '#014751'}
        case 1:
          return {'background-color': '#C6FFD2CC', 'color': '#014751'}
        case 2:
          return {'background-color': '#88FF9ACC', 'color': '#014751'}
        case 3:
          return {'background-color': '#43EA64CC', 'color': '#014751'}
        case 4:
          return {'background-color': '#00B12ECC', 'color': '#FFFFFF'}
        case 5:
          return {'background-color': '#008F4F', 'color': '#FFFFFF'}
        default:
          return {'background-color': '#999', 'color': '#FFFFFF'}
      }
    },
    keywordScoreToColorStyles (keywordScore) {
      if (keywordScore > 90)
        return { 'background-color': '#006B3E', 'color': '#FFFFFF' }
      else if (keywordScore > 80 && keywordScore <= 90)
        return { 'background-color': '#009254', 'color': '#FFFFFF' }
      else if (keywordScore > 70 && keywordScore <= 80)
        return { 'background-color': '#49BA8B', 'color': '#FFFFFF' }
      else if (keywordScore > 65 && keywordScore <= 70)
        return { 'background-color': '#65DDAB', 'color': '#014751' }
      else if (keywordScore > 60 && keywordScore <= 65)
        return { 'background-color': '#A6EFD1', 'color': '#014751' }
      else if (keywordScore > 50 && keywordScore <= 60)
        return { 'background-color': '#DAFFF0', 'color': '#014751' }
      else
        return { 'color': '#014751' }
    },
    getSiloUrl(siloKeyword) {
      return siloKeyword.keywordData.page || siloKeyword.keywordData.currentRankingUrl;
    },
    pageScoreTintColor(score) {
      return pageScoreTintColor(score)
    },
    removeKeyword(keywordId) {
      this.keywordsOngoingRemoval = [...this.keywordsOngoingRemoval, keywordId]
      this.$store.dispatch('removeKeywordFromSilo', keywordId)
      .then(response => {
        if (response?.data?.status === 'SUCCESS') {
          this.$emit('onKeywordRemoveSuccess', keywordId)
          return Promise.resolve()
        }
        this.$notify({
          group: 'info',
          type: 'error',
          text: response?.data?.msg || 'Failed!',
        });
      })
      .finally(() => {
        this.keywordsOngoingRemoval = this.keywordsOngoingRemoval.filter(k => k !== keywordId)
      })
    },
    restoreKeyword(keywordId) {
      this.keywordsOngoingRemoval = [...this.keywordsOngoingRemoval, keywordId]
      this.$store.dispatch('restoreSiloKeyword', keywordId)
      .then(response => {
        if (response?.data?.status === 'SUCCESS') {
          this.$emit('onKeywordRestoreSuccess', keywordId)
          return Promise.resolve()
        }
        this.$notify({
          group: 'info',
          type: 'error',
          text: response?.data?.msg || 'Failed!',
        });
      })
      .finally(() => {
        this.keywordsOngoingRemoval = this.keywordsOngoingRemoval.filter(k => k !== keywordId)
      })
    },
    updatePageCategory(siloKeywordId, value) {
      this.$store.dispatch('updateSiloKeyword', {
        siloKeywordId: siloKeywordId,
        body: {
          page_category: value,
        }
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.refreshData(true)
          }
        })
    },
    updateKeywordLabel(siloKeywordId, label) {
      this.keywordsOngoingLabelUpdate = [...this.keywordsOngoingLabelUpdate, siloKeywordId]
      this.$store.dispatch('updateSiloKeyword', {
        siloKeywordId: siloKeywordId,
        body: {
          keyword_type: label,
        }
      })
      .then(response => {
        if (response?.data?.status === 'SUCCESS') {
          return this.refreshData(true)
        } else {
          return Promise.reject()
        }
      })
      .finally(() => {
        this.keywordsOngoingLabelUpdate = this.keywordsOngoingLabelUpdate.filter(k => k !== siloKeywordId)
      })
    },
    gotoAiRun(runId) {
      if (!runId) return;

      window
        .open(
          `/#/content-editor?pageRunId=${runId}&editorMode=AI_MODE&selectTargetWC=true`,
          '_blank',
        )
        .focus();
    },
    gotoPageRuns(siloKeyword) {
      if (!siloKeyword.keywordData.isTopLevelKeyword) return;

      this.$store.commit('showLoading');
      this.$store
        .dispatch('createGuardRunFromSilo', siloKeyword.id)
        .then((response) => {
          if (response?.data?.status !== 'SUCCESS') return;
          this.createPOPReportTaskMonitor(siloKeyword.id, response.data.taskId)
        })
        .finally(() => this.$store.commit('hideLoading'));
    },
    gotoNewPopReport(siloKeyword) {
      const url = this.getSiloUrl(siloKeyword);
      
      const secondaryKeywords = this.secondaryTopLevelKeywords.filter(keyword => keyword.id !== siloKeyword.id)
      
      const externalData = {
        keyword: siloKeyword.keyword,
        isPageNotBuilt: !url,
        loc: siloKeyword.keywordData.locationCode,
        lang: siloKeyword.keywordData.languageCode,
        url,
        type: 'Silo',
        id: siloKeyword.id,
        ...(siloKeyword.keywordData.isTopLevelKeyword && secondaryKeywords.length)
          ? {
            useSecondaryKeywords: true,
            secondaryKeywords: secondaryKeywords.map(keyword => keyword.keyword)
          }
          : {}
      };

      this.$router.push({
        name: 'NewPage',
        params: {
          siteId: siloKeyword.siteId,
          externalData,
        }
      });
    },
    gotoPage(url) {
      this.$router.push(url)
    },
    async generatePDF () {
      // data init
      const siloKeywords = this.isWhiteGloveUser
        ? this.silo_keywords.filter(k => this.selectedKeywordIdsForOrder.indexOf(k.id) >= 0)
        : [...this.silo_keywords]
      const topLevelKeyword = filter(siloKeywords, 'keywordData.isTopLevelKeyword')?.[0]?.keyword
      
      const graphElement = document.getElementById('graph-main')
      const graphPNGData = await htmlToImage.toPng(graphElement)

      // pdf start
      const lineHeight = 1.5
      
      const doc = new jsPDF({
        lineHeight
      });

      let nextY = 0;
      
      // page layout
      const pageMargin = 14
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const innerPageWidth = pageWidth - (pageMargin * 2)
      const innerPageHeight = pageHeight - (pageMargin * 2)

      nextY += pageMargin - 4

      // header
      const { headerImg } = await getReportHeaderImage(this.$store, "silo_map")
      const headerImgProps = doc.getImageProperties(headerImg);

      const headerImgHeight = (headerImgProps.height * innerPageWidth) / headerImgProps.width; 
      
      doc.addImage(headerImg, 'png', pageMargin, nextY, innerPageWidth, headerImgHeight);

      nextY += headerImgHeight

      nextY += 10

      // your-keyword keyword label
      doc.setDrawColor(9, 52, 100);
      doc.setFillColor(9, 52, 100);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
    
      const text = 'YOUR KEYWORD'
      const textDimensions = doc.getTextDimensions(text)
      const boxPadding = 3
      const boxWidth = Math.round(textDimensions.w + (boxPadding * 2))
      const boxHeight = Math.round(textDimensions.h + (boxPadding * 2))

      doc.rect(pageMargin, nextY, boxWidth, boxHeight, 'FD')

      doc.text(text, pageMargin + boxPadding, nextY + boxPadding + textDimensions.h);

      nextY += boxHeight

      // top-level keyword box
      const box2Height = 30
      const box2LeftPadding = 70

      doc.setLineWidth(0.6);
      doc.setDrawColor(9, 52, 100)
      doc.setFillColor(230, 240, 255)
      doc.rect(pageMargin, nextY, innerPageWidth, box2Height, 'FD')

      doc.setTextColor(0, 0, 0);

      doc.text('Top Level Page Main Keyword', pageMargin + box2LeftPadding, nextY + 13);

      doc.setFont('helvetica', 'normal');
      if (topLevelKeyword) {
        doc.text(topLevelKeyword, pageMargin + box2LeftPadding, nextY + 20);
      }

      const keyIconImgProps = doc.getImageProperties('/static/orange-key-icon.png');
      const keyIconImgWidth = 15
      const keyIconImgHeight = (keyIconImgProps.height * keyIconImgWidth) / keyIconImgProps.width; 
      doc.addImage('/static/orange-key-icon.png', 'png', pageMargin + box2LeftPadding - keyIconImgWidth - 5, nextY + 10, keyIconImgWidth, keyIconImgHeight);

      nextY += box2Height
 
      nextY += 10

      // instructions box label
      const instructionsLabelBoxHeight = 20
      doc.setFillColor(9, 52, 100);
      doc.rect(pageMargin, nextY, innerPageWidth, instructionsLabelBoxHeight, 'FD')

      const instructionsLabelText = 'Instructions'

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      const instructionsLabelTextDimensions = doc.getTextDimensions(text)

      doc.text(
        instructionsLabelText,
        pageMargin + ((innerPageWidth - instructionsLabelTextDimensions.w) / 2) + 15,
        nextY + ((instructionsLabelBoxHeight - instructionsLabelTextDimensions.h) / 2) + instructionsLabelTextDimensions.h
      );

      const lightBulbIconImgProps = doc.getImageProperties('/static/light-bulb-icon.png');
      const lightBulbIconImgWidth = 10
      const lightBulbIconImgHeight = (lightBulbIconImgProps.height * lightBulbIconImgWidth) / lightBulbIconImgProps.width; 
      doc.addImage('/static/light-bulb-icon.png', 'png', pageMargin + 62, nextY + 5, lightBulbIconImgWidth, lightBulbIconImgHeight);

      nextY += instructionsLabelBoxHeight

      // instructions box

      const isBoldOpen = (arrayLength, valueBefore = false) => {
        const isEven = arrayLength % 2 === 0;
        return (valueBefore !== isEven);
      }

      const instructions = [
        'The purpose of a silo is to pass *authority* from and between Supporting Pages up to a Top Level Page.  Building up and using the authority from the pages of your own site is one of the safest, most effective ways to win more difficult keywords *without* heavily relying on external signals or backlinks. The more you can leverage your own site’s topical authority and the less you rely on external signals, the less susceptible your site is to updates and changes to *Google\'s algorithm*. You will give yourself the best chance for sustained, evergreen organic traffic growth.',
      ]

      const instructionsPadding = 12
      const instructionsTextWidth = innerPageWidth - instructionsPadding * 2

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);

      const splitInstructions = instructions.map(i => doc.splitTextToSize(i, instructionsTextWidth))
      const splitInstructionsHeights = splitInstructions.map(i => doc.getTextDimensions(i).h)


      const instructionsBoxHeight = (
        splitInstructionsHeights.reduce((a, b) => a + b, 0)
        + (instructionsPadding * (instructions.length + 1))
      )

      doc.setDrawColor(9, 52, 100);
      doc.setFillColor(230, 240, 255);
      doc.rect(pageMargin, nextY, innerPageWidth, instructionsBoxHeight, 'FD')

      let nextInstructionY = nextY + instructionsPadding;

      splitInstructions.forEach((splitText, index) => {
        
        let startX = pageMargin + instructionsPadding;
        let startY = nextInstructionY;

        doc.setFillColor(0, 0, 0);
        doc.circle(startX - 5, startY - 1.5, 1, 'F');
        
        doc.setFont('helvetica', 'normal');

        const startXCached = startX;
        let boldOpen = false;
        splitText.map((text, i) => {
            if (text) {
                const arrayOfNormalAndBoldText = text.split('*');
                arrayOfNormalAndBoldText.forEach((textItems, j) => {
                    doc.setFont('helvetica', boldOpen ? 'normal' : 'bold');
                    if (j % 2 === 0) {
                        doc.setFont('helvetica', boldOpen ? 'bold' : 'normal');
                    }
                    doc.text(textItems, startX, startY);
                    startX = startX + doc.getTextDimensions(textItems).w;
                });
                boldOpen = isBoldOpen(arrayOfNormalAndBoldText.length, boldOpen);
                startX = startXCached;
                startY += doc.getTextDimensions(text).h * (i < splitText.length - 1 ? lineHeight : 1);
            }
        });

        nextInstructionY = startY + instructionsPadding
      })

      // page break
      doc.addPage()
      nextY = pageMargin - 4

      // rest of insctrunctions

      const instructions2 = [
        'Ideally, Supporting Pages target  *lower competition*, easier to win keywords. As those Supporting Pages win more keywords, obtain more impressions, and clicks, they increase in authority. Through  *silo interlinking*, that authority is passed between the Supporting Pages, making each Supporting Page  *stronger*. Those pages with their increased authority then pass that authority to the Top Level Page.',  
        'If you have designated a page to be a Supporting Page, it should function as a Supporting Page *for only one Top Level Page*. You want to limit the internal links, as much as possible, to only those that establish the silo. Internal links, for this purpose, are links *within body content*. Links in the header, sidebar, or footer, do not matter.',
        'Another important point is *URL structure*. URL structure doesn\'t matter. This type of silo is a virtual silo, not a physical silo. Top Level Pages and Supporting Pages can be *anywhere* on your site. They can page pages, posts, or a combination of pages and posts. You  *do not need the parent/child relationship* of a physical silo between your target and Supporting Pages.',
        'Linking needs to be done in a very specific manner. All Supporting Pages will link to the Top Level Page with *the first link in the body content*. If possible, it is good to have *one link* from the Top Level Page going to any of the Supporting Pages. The Supporting Pages will *link to each other but will not daisy chain* all the way around.',
        'The link pattern should be as follows: A will link to B, B to A and C, C to B.',
        'A and C will not link to each other. The links between the Supporting Pages can be *anywhere in the body content*, just make sure they are *below* the link to the Top Level Page. As previously mentioned, *try to not have any other links*, internal or external, on the Supporting Pages.'
      ]

      const splitInstructions2 = instructions2.map(i => doc.splitTextToSize(i, (innerPageWidth - instructionsPadding * 2)))
      const splitInstructions2Heights = splitInstructions2.map(i => doc.getTextDimensions(i).h)


      const instructions2BoxHeight = (
        splitInstructions2Heights.reduce((a, b) => a + b, 0)
        + (instructionsPadding * (instructions2.length + 1))
      )

      doc.setDrawColor(9, 52, 100)
      doc.setFillColor(230, 240, 255)
      doc.rect(pageMargin, nextY, innerPageWidth, instructions2BoxHeight, 'FD')

      let nextInstruction2Y = nextY + instructionsPadding;

      splitInstructions2.forEach((splitText, index) => {
        
        let startX = pageMargin + instructionsPadding;
        let startY = nextInstruction2Y;

        doc.setFillColor(0, 0, 0);
        doc.circle(startX - 5, startY - 1.5, 1, 'F');
        
        doc.setFont('helvetica', 'normal');

        const startXCached = startX;
        let boldOpen = false;
        splitText.map((text, i) => {
            if (text) {
                const arrayOfNormalAndBoldText = text.split('*');
                arrayOfNormalAndBoldText.forEach((textItems, j) => {
                    doc.setFont('helvetica', boldOpen ? 'normal' : 'bold');
                    if (j % 2 === 0) {
                        doc.setFont('helvetica', boldOpen ? 'bold' : 'normal');
                    }
                    doc.text(textItems, startX, startY);
                    startX = startX + doc.getTextWidth(textItems);
                });
                boldOpen = isBoldOpen(arrayOfNormalAndBoldText.length, boldOpen);
                startX = startXCached;
                startY += doc.getTextDimensions(text).h * (i < splitText.length - 1 ? lineHeight : 1);
            }
        });

        nextInstruction2Y = startY + instructionsPadding
      })

      // page break
      doc.addPage()
      nextY = pageMargin - 4
      
      // silo map box label
      const siloMapLabelBoxHeight = 20
      doc.setFillColor(9, 52, 100)
      doc.rect(pageMargin, nextY, innerPageWidth, siloMapLabelBoxHeight, 'FD')

      const siloMapLabelText = 'Your Silo Map'

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      const siloMapLabelTextDimensions = doc.getTextDimensions(text)

      doc.text(
        siloMapLabelText,
        pageMargin + ((innerPageWidth - siloMapLabelTextDimensions.w) / 2) + 5,
        nextY + ((siloMapLabelBoxHeight - siloMapLabelTextDimensions.h) / 2) + siloMapLabelTextDimensions.h
      );

      nextY += siloMapLabelBoxHeight

      // silo map box
      const graphWidth = graphElement.scrollWidth * 0.175
      const graphHeight = graphElement.scrollHeight * 0.175

      const graphMarginHorizontal = (innerPageWidth - graphWidth) / 2
      const graphhMarginVertical = 10

      doc.setDrawColor(9, 52, 100)
      doc.setFillColor(255, 255, 255);
      doc.rect(pageMargin, nextY, innerPageWidth, graphHeight + graphhMarginVertical * 2, 'FD')

      doc.addImage(graphPNGData, 'png', pageMargin + graphMarginHorizontal, nextY + graphhMarginVertical, graphWidth, graphHeight);

      const timestamp = moment().format('YYYY_M_D_h_mm_ss')
      doc.save(`silo-keywords_${timestamp}.pdf`);

    },
    toggleSelectedSiloKeyword(id) {
      this.$store.commit('toggleSelectedSiloKeyword', id);
    },
    toggleAllSiloKeywords() {
      if (this.isAllSelected) this.$store.commit('resetSelectedSiloKeywords');
      else this.$store.commit('selectAllSiloKeywords', this.silo_keywords.map(silo_keyword => silo_keyword.id));
    },
    async toggleAllKeywordsForOrder() {
      this.$store.commit('showLoading');
      try {
        if (this.selectedKeywordsForOrder?.length === this.silo_keywords?.length) {
          // sequentially unselect all keywords for order
          for (const keyword of [...this.selectedKeywordsForOrder]) {
            await this.unselectKeywordForContentReportOrder(keyword, true);
          }
        } else {
          // sequentially select all keywords for order
          for (const keyword of [...this.silo_keywords]) {
            await this.selectKeywordForContentReportOrder(keyword, true);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$store.commit('hideLoading');
      }
    },
    handleDefaultSort(a, b) {
      if (a.totalPageImpressions == b.totalPageImpressions) {
        if (a.page == b.page) {
          if (a.impressions == b.impressions) {
            if (a.keyword < b.keyword) return -1;
            if (a.keyword > b.keyword) return 1;
          } else {
            return Number(b.impressions) - Number(a.impressions);
          }
        } else {
          if (a.page < b.page) {
            return -1;
          }
          if (a.page > b.page) {
            return 1;
          }
        }
      } else {
        return Number(b.totalPageImpressions) - Number(a.totalPageImpressions);
      }
    },
    onSort(label, direction) {
      (this.sortKey = label), (this.sortOrder = direction);
    },
    setColumnVisibility(targetKey, visible) {
      const updatedValue = this.hiddenColumnsKeys.filter(key => (
        key !== targetKey
      ))
      if (!visible) {
        updatedValue.push(targetKey)
      }

      const settingKey = `${this.siteId}-${this.isWhiteGloveUser ? this?.silo?.pageType : ''}`
      
      const updatedUserSettings = {
        ...this.user.settings,
        siloResultTableHiddenColumnsKeysPerSite: {
          ...this.user.settings.siloResultTableHiddenColumnsKeysPerSite || {},
          [settingKey]: updatedValue
        }
      }

      // eager update of settings state
      this.$store.commit('setUserSettings', updatedUserSettings)

      // persist in backend
      this.$store.dispatch('updateUserSettings', updatedUserSettings)
    },
    resetColumnVisibility() {
      let defaultHiddenColumns = HIDDEN_COLUMNS_KEYS_DEFAULT
      if (this.isWhiteGloveUser) {
        if (this?.silo?.pageType == 'new') {
          defaultHiddenColumns = HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_NEW_CONTENT
        }
        if (this?.silo?.pageType == 'existing') {
          defaultHiddenColumns = HIDDEN_COLUMNS_KEYS_DEFAULT_FOR_EXISTING_CONTENT
        }
      }

      const settingKey = `${this.siteId}-${this.isWhiteGloveUser ? this?.silo?.pageType : ''}`
      
      const updatedUserSettings = {
        ...this.user.settings,
        siloResultTableHiddenColumnsKeysPerSite: {
          ...this.user.settings.siloResultTableHiddenColumnsKeysPerSite || {},
          [settingKey]: defaultHiddenColumns
        }
      }

      // eager update of settings state
      this.$store.commit('setUserSettings', updatedUserSettings)

      // persist in backend
      this.$store.dispatch('updateUserSettings', updatedUserSettings)
    },
    isColumnVisible(targetKey) {
      if (this.tableColumns.map(c => c.key).indexOf(targetKey) < 0) {
        return false
      }
      if (this.hiddenColumnsKeys.indexOf(targetKey) >= 0) {
        return false
      }
      const meta = this.getColumnMeta(targetKey)
      if (meta?.isAdvancedMetric && !this.showAdvancedMetrics) {
        return false
      }
      return true
    },
    loadKeywordTrack () {
      this.$store.dispatch('getKeywordTrackForCampaign', this.campaignId)
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.keywordTrackData = response.data.keywordTrack
        }
      })
    },
    toggleTrackUntrackKeyword (shouldAddToTrack, item, listName=null) {
      let data = {}
      if (shouldAddToTrack) {
        data = {
          campaignId: this.campaignId,
          trackKeyword: item.keyword,
          trackData: item,
          listName,
          languageCode: item.keywordData.languageCode,
          locationCode: item.keywordData.locationCode
        }
        this.$store.commit('showLoading');
        this.$store.dispatch('addKeywordToTrack', data)
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.keywordTrackData = response.data.keywordTrack
            this.$notify({
              group: 'info', type: 'success',
              text: 'Successfully added keyword to track.'
            })
            if (this.showAddToNewTrackListModalFor) {
              this.showAddToNewTrackListModalFor = null;
            }
          } else {
            this.$notify({
              group: 'info', type: 'error',
              text: response.data.msg || 'Failed to add keyword to the track.'
            })
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
      } else {
        data = {
          campaignId: this.campaignId,
          trackKeyword: item.keyword,
        }

        this.$store.commit('showLoading');
        this.$store.dispatch('removeKeywordFromTrack', data)
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.keywordTrackData = response.data.keywordTrack
            this.$notify({
              group: 'info', type: 'success',
              text: 'Successfully removed keyword from track.'
            })
          } else {
            this.$notify({
              group: 'info', type: 'error',
              text: 'Failed to remove keyword from the track.'
            })
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
      }
    },
    selectKeywordForContentReportOrder(row, silent=false) {
      if (this.isPopAuthorityPlanView && !row.keywordData.isTopLevelKeyword && !this.paPackage.provisional) {
        // client-side package cap (the server enforces it too); while the site status is still loading the
        // package is the provisional default, whose cap may be smaller than the real one: the server decides
        const current = (this.silo.content_report_order?.selected_supporting_keyword_ids || []).filter(id => id !== row.id)
        if (current.length >= this.paSupportingCap) {
          this.$notify({
            group: 'info', type: 'warning',
            text: `Your ${this.paPackage.name} package covers ${this.paSupportingCap} Supporting keywords per month. Deselect one first, or upgrade your package.`,
          })
          return Promise.resolve()
        }
      }
      return this.$store.dispatch('updateSiloContentReportOrder', {
        orderId: this.silo.content_report_order.id,
        body: {
          ...row.keywordData.isTopLevelKeyword
            ?{
              selected_top_level_keyword_ids: (
                [
                  // NOTE: multiple top-level keywords allowed for quick content orders but only 1 is allowed for silo orders
                  ...this.silo.isForQuickContentOrder?(this.silo.content_report_order?.selected_top_level_keyword_ids || []).filter(id => id !== row.id):[],
                  ...[row.id]
                ]
              )
            }
            :{
              selected_supporting_keyword_ids: (
                [
                  ...(this.silo.content_report_order?.selected_supporting_keyword_ids || []).filter(id => id !== row.id),
                  ...[row.id]
                ]
              )
            }
        }
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.refreshData(silent)
          }
          if (response.data && response.data.msg) {
            this.$notify({ group: 'info', type: 'error', text: response.data.msg })
          }
        })
        .catch(error => {
          this.$notify({
            group: 'info', type: 'error',
            text: error?.response?.data?.msg || 'Could not update the keyword selection',
          })
        })
    },
    unselectKeywordForContentReportOrder(row, silent=false) {
      return this.$store.dispatch('updateSiloContentReportOrder', {
        orderId: this.silo.content_report_order.id,
        body: {
          ...row.keywordData.isTopLevelKeyword
            ?{
              selected_top_level_keyword_ids: (
                this.silo.content_report_order?.selected_top_level_keyword_ids || []).filter(id => id !== row.id
              )
            }
            :{
              selected_supporting_keyword_ids: (
                this.silo.content_report_order?.selected_supporting_keyword_ids || []).filter(id => id !== row.id
              )
            }
        }
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.refreshData(silent)
          }
          if (response.data && response.data.msg) {
            this.$notify({ group: 'info', type: 'error', text: response.data.msg })
          }
        })
        .catch(error => {
          this.$notify({
            group: 'info', type: 'error',
            text: error?.response?.data?.msg || 'Could not update the keyword selection',
          })
        })
    },
    getKeywordNoteTextFromOrder(id) {
      return (this.silo?.content_report_order?.keyword_notes || []).filter(note => note.keyword_id === id)[0]?.text || ''
    },
    getKeywordNoteTextFromUnsavedEdits(id) {
      return this.editedKeywordNotes.filter(note => note.keyword_id === id)[0]?.text || ''
    },
    getKeywordNoteInputValue(id) {
      if (this.hasUnsavedKeywordNoteChanges(id)) {
        return this.getKeywordNoteTextFromUnsavedEdits(id)
      }
      return this.getKeywordNoteTextFromOrder(id)
    },
    onKeywordNoteTextChange(keywordId, value) {
      this.editedKeywordNotes = [
        ...this.editedKeywordNotes.filter(note => note.keyword_id !== keywordId),
        ...value !== this.getKeywordNoteTextFromOrder(keywordId)
          ?[{keyword_id: keywordId, text: value}]
          :[]
      ]
    },
    hasUnsavedKeywordNoteChanges(keywordId) {
      return this.editedKeywordNotes.filter(note => note.keyword_id === keywordId).length > 0
    },
    saveKeywordNoteChanges(keywordId) {
      this.$store.dispatch('updateSiloContentReportOrder', {
        orderId: this.silo.content_report_order.id,
        body: {
          keyword_note: this.editedKeywordNotes.filter(note => note.keyword_id === keywordId)[0]
        }
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.refreshData()
          }
        })
    },

    // keyword data task monitors
    refreshKeywordTaskMonitors () {
      this.clearAllKeywordTaskIntervals()
      this.silo_keywords.forEach(keywordRow => {
        if (keywordRow.keywordResearchDataTaskId) {
          this.createKeywordTaskMonitor(keywordRow.id, keywordRow.keywordResearchDataTaskId)
        }
      })
    },
    createKeywordTaskMonitor (keywordId, taskId) {
      this.keywordTaskIntervals = {

        ...this.keywordTaskIntervals,

        [keywordId]: setInterval(() => {
          this.$store.dispatch('getTaskStatus', taskId)
          .then(response => {
            if (response.data) {
              if (response.data.status == 'PENDING') {
                // ignore
                // Celery's PENDING state may as well mean that the task is simply unknwon
                // (i.e. could be a really pending task, but could also be an invalid/non-existing task id or could be a completed but forgotten task)
              } else if (response.data.status == 'PROGRESS') {
                // do nothing
              } else if (response.data.status == 'SUCCESS') {
                this.clearKeywordTaskInterval(keywordId);
                this.refreshData(true)
              } else if (response.data.status == 'FAILURE') {
                this.clearKeywordTaskInterval(keywordId);
                this.refreshData(true)
              }
            }
          })
        }, 3000)
      }
    },
    clearKeywordTaskInterval (keywordId) {
      if (this.keywordTaskIntervals[keywordId]) {
        clearInterval(this.keywordTaskIntervals[keywordId])
        this.keywordTaskIntervals = Object.fromEntries(
          Object.entries(this.keywordTaskIntervals).filter(([key]) => key !== keywordId)
        )
      }
    },
    clearAllKeywordTaskIntervals () {
      Object.entries(this.keywordTaskIntervals).forEach(([id]) => {
        this.clearKeywordTaskInterval(id)
      })
    },

    // POP Report task monitors
    refreshPOPReportTaskMonitors () {
      this.clearAllPOPReportTaskIntervals()
      this.silo_keywords.forEach(keywordRow => {
        if (['PENDING', 'PROGRESS'].includes(keywordRow?.pageRunResult?.status) && keywordRow?.pageRunResult?.taskId) {
          this.createPOPReportTaskMonitor(keywordRow.id, keywordRow?.pageRunResult?.taskId)
        }
      })
    },
    createPOPReportTaskMonitor (keywordId, taskId) {
      this.POPReportTaskIntervals = {

        ...this.POPReportTaskIntervals,

        [keywordId]: {
          interval: setInterval(() => {
            this.$store.dispatch('getTaskStatus', taskId)
            .then(response => {
              if (response.data) {
                if (response.data.status == 'PENDING') {
                  // ignore
                  // Celery's PENDING state may as well mean that the task is simply unknwon
                  // (i.e. could be a really pending task, but could also be an invalid/non-existing task id or could be a completed but forgotten task)
                  this.updatePOPReportTaskMonitor(keywordId, {
                    value: 5,
                    status: 'PENDING'
                  })
                } else if (response.data.status == 'PROGRESS') {
                  this.updatePOPReportTaskMonitor(keywordId, {
                    value: response.data.value,
                    status: 'PROGRESS'
                  })
                } else if (response.data.status == 'SUCCESS') {
                  this.clearPOPReportTaskInterval(keywordId);
                  this.refreshData(true)
                } else if (response.data.status == 'FAILURE') {
                  this.clearPOPReportTaskInterval(keywordId);
                  this.refreshData(true)
                }
              }
            })
          }, 3000),
          status: {
            value: 5,
            status: 'PENDING'
          }
        }
      }
    },
    updatePOPReportTaskMonitor (keywordId, status) {
      this.POPReportTaskIntervals = {

        ...this.POPReportTaskIntervals,

        [keywordId]: {
          interval: this.POPReportTaskIntervals?.[keywordId]?.interval,
          status
        }
      }
    },
    clearPOPReportTaskInterval (keywordId) {
      if (this.POPReportTaskIntervals[keywordId]) {
        if (this.POPReportTaskIntervals[keywordId]?.interval) {
          clearInterval(this.POPReportTaskIntervals[keywordId].interval)
        }
        this.POPReportTaskIntervals = Object.fromEntries(
          Object.entries(this.POPReportTaskIntervals).filter(([key]) => key !== keywordId)
        )
      }
    },
    clearAllPOPReportTaskIntervals () {
      Object.entries(this.POPReportTaskIntervals).forEach(([id]) => {
        this.clearPOPReportTaskInterval(id)
      })
    },
    getContentReportType(row) {
      let reportKey = null
      if (row.keywordData.isTopLevelKeyword) {
        reportKey = this.silo?.content_report_order?.selected_top_level_option_key
      } else if (row.keywordData.isSupportingKeyword) {
        reportKey = this.silo?.content_report_order?.selected_supporting_option_key
      }
      return this.contentReportPackages.filter(p => p.key === reportKey)?.[0]?.label
    },
    handleViewContent(siloKeyword, order) {
      // NOTE: any updates to this function should be reflected in the SitesSingleView/WhiteGlove/AllContent.vue handleViewContent function
      if (
        siloKeyword.keywordData.isTopLevelKeyword
        && order?.selected_top_level_option_key === 'TOP_LEVEL_CONTENT_UPGRADE'
      ) {
        this.$refs['silo-action-menu-' + siloKeyword.id].getSiloTopLevelReport()
        return null
      }

      let secretKey = siloKeyword.pageRunResult.secretKey;
      if (!secretKey) return;
      let url = `/content-editor?secretKey=${secretKey}&siloKeywordId=${siloKeyword.id}`
      if (this.selectedSubAccountRelation) {
        url += `&relId=${this.selectedSubAccountRelation.id}`
      }
      url += `&isWhiteGloveUser=true&isOrderedReport=true&orderedReportType=${this.getContentReportTypeId(siloKeyword, order)}`
      // open in new tab
      window.open('/#' + url, '_blank')
      return null
    },
    getContentReportTypeId(row) {
      let reportKey = null
      if (row.keywordData.isTopLevelKeyword) {
        reportKey = this.silo?.content_report_order?.selected_top_level_option_key
      } else if (row.keywordData.isSupportingKeyword) {
        reportKey = this.silo?.content_report_order?.selected_supporting_option_key
      }
      return this.contentReportPackages.filter(p => p.key === reportKey)?.[0]?.report_id
    }
  },
  mounted () {
    this.loadKeywordTrack()

    this.$store.dispatch('getAvailableSiloContentReportPackages')
    .then(response => {
      if (response.data && response.data.status == 'SUCCESS') {
        this.contentReportPackages = response.data.packages;
      }
    })
  },
  destroyed () {
    this.clearAllKeywordTaskIntervals()
    this.clearAllPOPReportTaskIntervals()
  }
};
</script>

<style lang="scss" scoped>
@import './silo-research.scss';
.actions-menu .v-menu__content {
  background-color: transparent !important;
  border: none !important; 
  box-shadow: none !important;
  max-width: 300px !important;
}
.disabled-field {
  background-color: var(--v-normalLightGray-base) !important;
  cursor: not-allowed;
  color: #A7A7A7 !important;
}
[role="menu"]:has(.silo-report-menu) {
  box-shadow: 0px 2.563px 20.825px 0px rgba(0, 0, 0, 0.12) !important;
}
.pop-report-menu-item:hover {
  background-color: rgba(161, 161, 161, 0.12) !important;
}

.silo-white-glove-header {
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: space-between;

  .container-launch {
    display: flex;
    background-color: var(--v-lightGray-base);;
    border-radius: 0.313rem;
    border: 1px solid var(--v-grayColor-base);
  
    .width704 {
      width: 44rem;
    }
  
    .font12 {
      font-size: 0.75rem;
      color: var(--v-primary-base);
      font-weight: 400;
    }
  
    .font20 {
      font-size: 1.25rem;
      font-weight: 800;
      color: var(--v-primary-base);
    }
  
    .font1rem {
      font-size: 1rem !important;
      font-weight: 400 !important;
      color: var(--v-primary-base);
      line-height: 1.6rem;
    }
  
    .font15 {
      font-size: 0.938rem;
      font-weight: 700;
    }

    .height38 {
      height: 2.375rem !important;
    }

    .padding8 {
      padding: 8px 12px !important;
    }
  
    .silo-structure {
      padding-top: 3px;
    }
  
    .cross-container {
      margin-top: -10px;
    }
  }
}

.content-order-keyword-select {
  border-radius: 40px;
  padding: 0 8px !important;

  span {
    font-weight: 700 !important;
  }

  .material-icons {
    margin-right: 0;
  }

  &.selected {
    border: 1.3px solid #00844D;
    background: #EFFFFF;
    color: #00844D !important;
    padding: 0 6px !important;
    &.v-btn--disabled {
      color: #00844D !important;
    }
  }
}

.ordered-label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.25;

  &__status-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }

  &__dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #25a97a;
  }

  &__status {
    font-size: 0.875rem;
    font-weight: 600;
    color: #25a97a;
    white-space: nowrap;
  }

  &__type {
    font-size: 0.75rem;
    font-weight: 400;
    color: #3a3a3a;
    white-space: nowrap;
  }
}

.content-order-keyword-note-btn {
  position: relative;

  .badge {
    position: absolute;
    display: inline-block;
    top: -6px;
    right: -6px;
    background: #FF5700;
    color: #fff;
    width: 16px;
    height: 16px;
    line-height: 16px;
    text-align: center;
    border-radius: 16px;
    font-size: 10px;
  }
}

.page-category-dropdown {
  .page-category-dropdown-button {
    font-weight: 700;
    border-width: 1.3px;
    border-color: #014751;
    min-width: 130px;
    justify-content: space-between;
    height: 28px;
    font-weight: initial;
    margin-right: 20px;
  }

  &:not(.is-disabled) .dropdown-menu .dropdown-item.is-disabled, &:not(.is-disabled) .dropdown-menu .has-link a.is-disabled {
    opacity: 0.8 !important;
    cursor: default;
  }
}

::v-deep {
  .wg-result-select-all-checkbox {
    label {
      color: var(--v-mainColor-base);
      font-size: 0.9rem;
    }

    .v-input--selection-controls__input {
      border: 1px solid #999999 !important;
      border-radius: 4px;
      height: 20px !important;
      width: 20px !important;
    }

    &.v-input--is-label-active {
      .v-icon {
        font-size: 16px !important;
      }
    }
  }

  .voltage-table table {
    border-radius: 10px;
  }

  #silo-result-table {
    .b-table {
      th {
        background-color: var(--v-gray7Color-base);
        font-weight: 700;
      }
    }
  }
}

/* POP Authority content plan (design pa-plan.jsx table): white .92rem / 800 headers, 18px 14px cells
   at .95rem, lavender group rows, rows left out of a billed month faded, "Write" checkbox
   (pa.css .pa-check) */
.voltage-result-area--pa {
  ::v-deep #silo-result-table {
    .b-table .table {
      border: 1px solid #e4e4e4;
      border-radius: 10px;
      overflow: hidden;
    }

    .b-table th {
      background-color: #fff;
      font-size: 0.92rem;
      font-weight: 800;
      padding: 16px 14px;
      color: var(--v-mainColor-base);
      white-space: nowrap;
    }

    .b-table td {
      padding: 18px 14px;
      font-size: 0.95rem;
      border-top: 1px solid #e4e4e4;
      vertical-align: middle;
    }

    .b-table tr.keyword-type-group-header td {
      background: #f7f5fb;
      padding: 14px;
      font-weight: 800;
      font-size: 0.98rem;
    }

    .b-table tr.pa-row--dimmed td {
      opacity: 0.5;
    }
  }

  .ordered-label__status {
    font-size: 0.95rem;
    font-weight: 700;
  }

  .ordered-label__type {
    font-size: 0.82rem;
    color: #6b7b7e;
  }
}

.pa-write {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &__box {
    width: 19px;
    height: 19px;
    border: 2px solid #ccc;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    transition: background 0.15s, border-color 0.15s;

    .material-icons {
      font-size: 14px;
      color: #fff;
    }
  }

  &--sel .pa-write__box {
    background: var(--v-profileAnchorColor-base);
    border-color: var(--v-profileAnchorColor-base);
  }

  &--locked {
    cursor: default;
  }

  &--locked:not(.pa-write--sel) .pa-write__box {
    opacity: 0.4;
  }

  &:focus-visible .pa-write__box {
    border-color: var(--v-profileAnchorColor-base);
  }
}

</style>
